import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { CalendarIcon, Plus, Minus, ShoppingCart, User, Utensils, CreditCard, CheckCircle, Download, Upload, FileText, ChefHat, TrendingUp, Sparkles, ChevronDown, ChevronUp, Search, X, Home, BarChart3, Edit, Eye, EyeOff, Filter, Calendar as CalendarFilter, AlertCircle, LogOut, Store, Users, ArrowLeft, Trash2, Mail, Crown, RefreshCw, XCircle } from "lucide-react";
import { format } from "date-fns";
import { cn } from "./lib/utils";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList } from 'recharts';

// Import utilities
import { API, BACKEND_URL } from './utils/constants';
import { getImageUrl, formatMonDD } from './utils/formatters';

// Import auth components
import RoleSelection from './components/auth/RoleSelection';
import SignUpPage from './components/auth/SignUpPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';

// Import subscription components
import TrialBanner from './components/subscription/TrialBanner';
import PaymentSelectionModal from './components/subscription/PaymentSelectionModal';
import UpgradeModal from './components/subscription/UpgradeModal';
import ManageSubscriptionModal from './components/subscription/ManageSubscriptionModal';
import SubscriptionSuccessPage from './components/subscription/SubscriptionSuccessPage';
import SubscriptionCancelPage from './components/subscription/SubscriptionCancelPage';

// Import page components
import ReportsPage from './components/pages/ReportsPage';
import PublicLandingPage from './components/pages/PublicLandingPage';
import LandingPage from './components/pages/LandingPage';
import UserLandingPage from './components/pages/UserLandingPage';
import MenuManagementPage from './components/pages/MenuManagementPage';
import StoreSetupPage from './components/pages/StoreSetupPage';
import TeamManagementPage from './components/pages/TeamManagementPage';

// Import shared components
import WelcomeHintModal from './components/shared/WelcomeHintModal';
import FloatingContactButton from './components/shared/FloatingContactButton';
import { AdminPasswordChange, StoreOwnerPasswordChangeForm, UserPasswordChange, ForcedPasswordChangeModal } from './components/shared/PasswordChangeModals';

// Import order components
import OrderView from './components/orders/OrderView';
import { MenuItemSizeSelector, MenuItemRow, MenuItemCard } from './components/orders/MenuComponents';

// Import admin components
import SuperAdminPortal from './components/admin/SuperAdminPortal';
import ImpersonationModal from './components/admin/ImpersonationModal';
import ImpersonationBanner from './components/shared/ImpersonationBanner';
import AuditLogPage from './components/pages/AuditLogPage';
import CustomerManagementPage from './components/pages/CustomerManagementPage';
import FeedbackPage from './components/pages/FeedbackPage';

function App() {
  // View state for public pages
  const [publicView, setPublicView] = useState('landing'); // 'landing', 'signin', 'signup'
  const [showInlineSignIn, setShowInlineSignIn] = useState(() => {
    // Check if redirected from logout
    return localStorage.getItem('showSignInPanel') === 'true';
  });
  
  // Clear the flag after checking
  useEffect(() => {
    if (showInlineSignIn) {
      localStorage.removeItem('showSignInPanel');
    }
  }, [showInlineSignIn]);
  
  // Check for signin query parameter (from payment success)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('signin') === 'true') {
      setPublicView('signin');
      // Clean up URL
      window.history.replaceState({}, '', '/');
    }
  }, []);
  
  // GLOBAL RULE: Remove "Made by Emergent" badge from ALL pages
  useEffect(() => {
    const removeEmergentBadge = () => {
      // Remove all iframes with emergent in src
      document.querySelectorAll('iframe').forEach(iframe => {
        if (iframe.src && iframe.src.toLowerCase().includes('emergent')) {
          iframe.remove();
        }
      });
      
      // Remove any element containing "emergent" text in fixed/absolute position
      document.querySelectorAll('*').forEach(el => {
        const style = window.getComputedStyle(el);
        const text = el.textContent?.toLowerCase() || '';
        if ((style.position === 'fixed' || style.position === 'absolute') && 
            (text.includes('emergent') || text.includes('made by'))) {
          el.remove();
        }
      });
      
      // Remove any links to emergent
      document.querySelectorAll('a').forEach(link => {
        if (link.href && link.href.toLowerCase().includes('emergent')) {
          link.remove();
        }
      });
    };
    
    // Run immediately and every 500ms to catch dynamically added badges
    removeEmergentBadge();
    const interval = setInterval(removeEmergentBadge, 500);
    
    return () => clearInterval(interval);
  }, []);
  
  // New Multi-Tenant Authentication State
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    return saved ? JSON.parse(saved) : null;
  });
  const [sessionToken, setSessionToken] = useState(() => {
    return localStorage.getItem('sessionToken') || null;
  });
  const [currentStore, setCurrentStore] = useState(() => {
    const saved = localStorage.getItem('currentStore');
    return saved ? JSON.parse(saved) : null;
  });
  
  // Helper function to get current store ID
  const getStoreId = () => {
    return currentUser?.storeId || currentStore?.storeId || currentStore?.id;
  };
  
  // Role state with proper differentiation
  const [userRole, setUserRole] = useState(() => {
    if (currentUser) {
      if (currentUser.role === 'super_admin') return 'super_admin';
      if (currentUser.role === 'store_owner') return 'store_owner';  // Full access to Admin Portal
      if (currentUser.role === 'store_admin') return 'store_admin';  // Manager - Staff Portal + edit confirmed
      if (currentUser.role === 'store_staff') return 'store_staff';  // Staff Portal view only
      return 'user';
    }
    return null;
  });

  // ===== IMPERSONATION STATE =====
  // When super admin impersonates a user, we save their original session
  // to localStorage under `originalUser`. Subsequent API calls are sent
  // as the target user (X-User-Email = target.email) plus a
  // X-Impersonated-By header carrying the original super admin email
  // for audit attribution.
  const [originalUser, setOriginalUser] = useState(() => {
    const saved = localStorage.getItem('originalUser');
    return saved ? JSON.parse(saved) : null;
  });
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);

  // Install a global axios interceptor that auto-injects auth headers
  // (X-User-Email, X-User-Role, X-Impersonated-By) on every API call so
  // we do not have to thread them through every fetch. Runs once on
  // mount; reads fresh values from localStorage each request.
  useEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      try {
        const url = (config.url || '').toString();
        if (!url.includes('/api/')) return config;
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null');
        const orig = JSON.parse(localStorage.getItem('originalUser') || 'null');
        config.headers = config.headers || {};
        if (user?.email && !config.headers['X-User-Email'] && !config.headers['x-user-email']) {
          config.headers['X-User-Email'] = user.email;
        }
        if (user?.role && !config.headers['X-User-Role'] && !config.headers['x-user-role']) {
          config.headers['X-User-Role'] = user.role;
        }
        if (orig?.email && !config.headers['X-Impersonated-By']) {
          config.headers['X-Impersonated-By'] = orig.email;
        }
      } catch (e) {
        // best effort; never block requests on header injection
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  // ===== IMPERSONATION HANDLERS =====
  const handleStartImpersonation = (targetUser, targetStore) => {
    if (!currentUser || currentUser.role !== 'super_admin') {
      toast.error('Only super admins can impersonate.');
      return;
    }
    if (!targetUser) return;
    // Save super admin session under originalUser
    const saved = currentUser;
    localStorage.setItem('originalUser', JSON.stringify(saved));
    setOriginalUser(saved);
    // Swap currentUser to target
    localStorage.setItem('currentUser', JSON.stringify(targetUser));
    setCurrentUser(targetUser);
    if (targetStore) {
      localStorage.setItem('currentStore', JSON.stringify(targetStore));
      setCurrentStore(targetStore);
    } else {
      localStorage.removeItem('currentStore');
      setCurrentStore(null);
    }
    // Map role
    if (targetUser.role === 'store_owner') setUserRole('store_owner');
    else if (targetUser.role === 'store_admin') setUserRole('store_admin');
    else if (targetUser.role === 'store_staff') setUserRole('store_staff');
    else setUserRole('user');
    setCurrentView('landing');
    toast.success(`Now impersonating ${targetUser.name || targetUser.email}`);
  };

  const handleStopImpersonation = async () => {
    if (!originalUser) return;
    try {
      // best-effort audit record of the stop event
      await axios.post(`${API}/admin/stop-impersonation`).catch(() => {});
    } catch (e) {
      // ignore
    }
    // Restore original super admin session
    localStorage.setItem('currentUser', JSON.stringify(originalUser));
    setCurrentUser(originalUser);
    localStorage.removeItem('originalUser');
    setOriginalUser(null);
    localStorage.removeItem('currentStore');
    setCurrentStore(null);
    setUserRole('super_admin');
    setCurrentView('landing');
    toast.info('Exited impersonation');
  };
  
  // Legacy password states (kept for old login fallback)
  const [adminPassword, setAdminPassword] = useState(() => {
    const saved = localStorage.getItem('adminPassword');
    if (!saved) {
      localStorage.setItem('adminPassword', 'Bawarchi@4950');
      return 'Bawarchi@4950';
    }
    return saved;
  });
  const [userPassword, setUserPassword] = useState(() => {
    const saved = localStorage.getItem('userPassword');
    if (!saved) {
      localStorage.setItem('userPassword', 'catering');
      return 'catering';
    }
    return saved;
  });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showUserPasswordChange, setShowUserPasswordChange] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false); // Force password change on first login
  
  // Dropdown menu states
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showTasksMenu, setShowTasksMenu] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showWelcomeHint, setShowWelcomeHint] = useState(false);
  
  // Subscription states
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showManageSubscriptionModal, setShowManageSubscriptionModal] = useState(false);
  const [upgradeModalSource, setUpgradeModalSource] = useState(null); // Track where upgrade modal was opened from
  
  // Debug upgrade modal
  useEffect(() => {
    console.log('🔵 showUpgradeModal changed:', showUpgradeModal);
  }, [showUpgradeModal]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly or yearly
  
  // Payment selection modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingSignupData, setPendingSignupData] = useState(null); // Store user/token after signup
  
  // Navigation state
  const [currentView, setCurrentView] = useState('landing'); // 'landing', 'orderForm', 'reports', 'menuManagement', 'teamManagement', 'storeSetup'
  const [previousView, setPreviousView] = useState('landing'); // Track where we came from
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [viewingOrderId, setViewingOrderId] = useState(null); // For read-only view
  const [viewingOrdersList, setViewingOrdersList] = useState([]); // List of orders for navigation
  const [viewingOrderIndex, setViewingOrderIndex] = useState(0); // Current index in orders list
  
  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [hasVisitedFinalReview, setHasVisitedFinalReview] = useState(false); // Track if user reached step 5
  
  // Step configuration - New order: Event Duration → Menu → Cart → Customer Info → Final Review
  // Steps: 1=Event Duration, 2=Menu Selection, 3=Cart Review, 4=Customer Info, 5=Final Review with Pricing
  const [loading, setLoading] = useState(false);
  const [menuInitialized, setMenuInitialized] = useState(false);

  // Form data states
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    occasion: '',
    adults: 1,
    kids: 0,
    start_date: null,
    end_date: null,
    day_meal_selections: [],
    address: '',
    prepared_by: '',
    lead_employee: ''
  });
  
  const [updateReason, setUpdateReason] = useState(''); // Separate state for update reason
  
  // Multi-day event states
  const [eventDays, setEventDays] = useState([]);
  const [selectedDayMeal, setSelectedDayMeal] = useState({ day: 1, meal: 'breakfast' });
  
  // Multi-day sub-order states (for splitting orders per day/meal)
  const [isMultiDayOrder, setIsMultiDayOrder] = useState(false);
  const [dayMealHeadcounts, setDayMealHeadcounts] = useState({}); // { "1-lunch": 50, "1-dinner": 30, "2-breakfast": 40 }

  // Cart item notes editing state
  const [editingNoteIndex, setEditingNoteIndex] = useState(null); // Track which cart item's note is being edited

  // Custom item states
  const [showCustomItemModal, setShowCustomItemModal] = useState(false);
  const [allMenuItemsForDuplicateCheck, setAllMenuItemsForDuplicateCheck] = useState([]); // For duplicate detection
  const [customItem, setCustomItem] = useState({
    name: '',
    category: 'CUSTOM',
    protein_type: '',
    tray_size: 'small',
    quantity: 1,
    total_price: '',
    unit_of_measure: 'Tray',
    notes: '',
    customCategory: '',
    customUOM: '',
    customSize: '',
    pricing: { qt: '', mht: '', ht: '', mlt: '', lt: '', per_piece: '' },
    traySelection: {
      qt: { add: false, qty: 1 },
      mht: { add: false, qty: 1 },
      ht: { add: false, qty: 1 },
      mlt: { add: false, qty: 1 },
      lt: { add: false, qty: 1 },
    },
    pieceQty: 1,
    pieceAdd: true,
  });
  const [customSizes, setCustomSizes] = useState([
    { size: '', price: '', addToCart: false }
  ]);

  // Occasion suggestions for autocomplete
  const [occasionSuggestions, setOccasionSuggestions] = useState([]);
  const [showOccasionSuggestions, setShowOccasionSuggestions] = useState(false);
  const [filteredOccasionSuggestions, setFilteredOccasionSuggestions] = useState([]);

  // Fetch occasion suggestions when store is loaded
  useEffect(() => {
    const fetchOccasionSuggestions = async () => {
      if (!currentStore?.storeId && !currentStore?.id) return;
      try {
        const storeId = currentStore.storeId || currentStore.id;
        const response = await axios.get(`${API}/suggestions/occasions`, {
          params: { store_id: storeId }
        });
        setOccasionSuggestions(response.data?.suggestions || []);
      } catch (err) {
        console.error('Failed to fetch occasion suggestions:', err);
      }
    };
    fetchOccasionSuggestions();
  }, [currentStore]);

  // Filter occasion suggestions based on input
  useEffect(() => {
    if (customerInfo.occasion.length > 0) {
      const filtered = occasionSuggestions.filter(s => 
        s.toLowerCase().includes(customerInfo.occasion.toLowerCase())
      );
      setFilteredOccasionSuggestions(filtered);
    } else {
      setFilteredOccasionSuggestions(occasionSuggestions);
    }
  }, [customerInfo.occasion, occasionSuggestions]);

  // Update selectedDayMeal when eventDays change
  useEffect(() => {
    if (eventDays.length > 0) {
      const firstDay = eventDays[0];
      if (firstDay.selected_meals.length > 0) {
        setSelectedDayMeal({
          day: firstDay.day_number,
          meal: firstDay.selected_meals[0]
        });
      }
    }
  }, [eventDays]);

  // Listen for custom event to open upgrade modal from ManageSubscriptionModal
  useEffect(() => {
    const handleOpenUpgradeModal = (event) => {
      const source = event.detail?.source || null;
      setUpgradeModalSource(source);
      setShowUpgradeModal(true);
    };
    
    window.addEventListener('openUpgradeModal', handleOpenUpgradeModal);
    return () => {
      window.removeEventListener('openUpgradeModal', handleOpenUpgradeModal);
    };
  }, []);


  // Check for password reset token in URL
  useEffect(() => {
    // Debug: Log all URL parts
    console.log('🔍 URL Debug:', {
      fullURL: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash
    });
    
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    
    console.log('🔍 Password Reset Token Detection:', { 
      hasToken: !!resetToken, 
      token: resetToken ? resetToken.substring(0, 10) + '...' : 'NULL',
      currentPublicView: publicView,
      allParams: Array.from(urlParams.entries())
    });
    
    if (resetToken) {
      console.log('✅ Reset token found! Setting view to resetPassword');
      setPublicView('resetPassword');
    } else {
      console.log('❌ No reset token found in URL');
    }
  }, []);


  const [cateringType, setCateringType] = useState('');
  const [cateringTypeDescription, setCateringTypeDescription] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loadingMenuItems, setLoadingMenuItems] = useState(false);
  const [cart, setCart] = useState([]);
  const [serviceFee, setServiceFee] = useState(0);
  const [serviceFeeType, setServiceFeeType] = useState('none'); // 'none', '5%', '10%', '12%', '15%', 'custom'
  const [customServiceFee, setCustomServiceFee] = useState(0);
  const [discountType, setDiscountType] = useState('none');
  const [discountPercentage, setDiscountPercentage] = useState(''); // '', '5%', '10%', '12%', '15%', 'custom'
  const [discountAmount, setDiscountAmount] = useState(0);
  const [customDiscountAmount, setCustomDiscountAmount] = useState(0);
  const [addingToCart, setAddingToCart] = useState(null);
  const [advancePayment, setAdvancePayment] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [orderStatus, setOrderStatus] = useState('quote');
  const [trayRecommendations, setTrayRecommendations] = useState({});
  const [submittedOrderId, setSubmittedOrderId] = useState(null);
  const [recommendedPackages, setRecommendedPackages] = useState([]);
  const [popularItems, setPopularItems] = useState([]);
  const [serviceFeeDescription, setServiceFeeDescription] = useState('');
  const [recommendationsCollapsed, setRecommendationsCollapsed] = useState(false);
  const [popularItemsCollapsed, setPopularItemsCollapsed] = useState(false);
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  
  // Backdating feature removed

  // Function to extract unique UOMs from menu items
  const getUniqueUOMs = () => {
    if (!menuItems || menuItems.length === 0) {
      console.log('getUniqueUOMs: No menu items available', menuItems?.length);
      return [];
    }
    const uoms = menuItems
      .map(item => item.unit_of_measure)
      .filter(uom => uom && uom.trim() !== '');
    const uniqueUOMs = [...new Set(uoms)].sort();
    console.log('getUniqueUOMs: Found UOMs', uniqueUOMs);
    return uniqueUOMs;
  };

  // Function to extract unique sizes from pricing objects, filtered by UOM
  const getSizesByUOM = (selectedUOM) => {
    if (!menuItems || menuItems.length === 0) {
      console.log('getSizesByUOM: No menu items available');
      return [];
    }
    
    // Filter items by UOM
    const filteredItems = menuItems.filter(item => item.unit_of_measure === selectedUOM);
    console.log(`getSizesByUOM: Found ${filteredItems.length} items with UOM '${selectedUOM}'`);
    
    // Extract all pricing keys from filtered items
    const allSizes = new Set();
    filteredItems.forEach(item => {
      if (item.pricing && typeof item.pricing === 'object') {
        Object.keys(item.pricing).forEach(size => allSizes.add(size));
      }
    });
    
    const sizes = Array.from(allSizes).sort();
    console.log(`getSizesByUOM: Found sizes for '${selectedUOM}':`, sizes);
    return sizes;
  };

  // Initialize menu on component mount
  useEffect(() => {
    initializeMenu();
  }, []);

  const initializeMenu = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/initialize-menu`);
      const storeId = getStoreId();
      const categoriesRes = await axios.get(`${API}/categories${storeId ? `?store_id=${storeId}` : ''}`);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      setMenuInitialized(true);
    } catch (error) {
      console.error('Failed to initialize menu:', error);
      setCategories([]); // Ensure categories is always an array
    } finally {
      setLoading(false);
    }
  };

  // Fetch subscription status
  const fetchSubscriptionStatus = async () => {
    if (!currentUser) return;
    
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: {
          'X-User-Email': currentUser.email,
          'X-User-Role': userRole
        }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  useEffect(() => {
    if (currentUser && userRole) {
      fetchSubscriptionStatus();
    }
  }, [currentUser, userRole]);


  const fetchMenuItems = async (category) => {
    try {
      setLoadingMenuItems(true);
      setMenuItems([]); // Clear previous items immediately
      const storeId = getStoreId();
      const response = await axios.get(`${API}/menu-items?category=${encodeURIComponent(category)}${storeId ? `&store_id=${storeId}` : ''}`);
      setMenuItems(response.data);
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
      setMenuItems([]); // Clear items on error too
    } finally {
      setLoadingMenuItems(false);
    }
  };

  // Fetch all menu items for search
  const fetchAllMenuItems = async () => {
    try {
      setLoadingMenuItems(true);
      const allItems = [];
      const storeId = getStoreId();
      for (const category of categories) {
        const response = await axios.get(`${API}/menu-items?category=${encodeURIComponent(category)}${storeId ? `&store_id=${storeId}` : ''}`);
        allItems.push(...response.data);
      }
      setMenuItems(allItems);
    } catch (error) {
      console.error('Failed to fetch all menu items:', error);
      setMenuItems([]);
    } finally {
      setLoadingMenuItems(false);
    }
  };

  // Filter menu items by search query
  const getFilteredMenuItems = () => {
    if (!menuSearchQuery.trim()) {
      return menuItems;
    }
    
    const query = menuSearchQuery.toLowerCase();
    return menuItems.filter(item => 
      item.name.toLowerCase().includes(query)
    );
  };

  const getTrayRecommendations = async () => {
    try {
      const response = await axios.post(`${API}/calculate-tray-recommendation`, {
        adults: customerInfo.adults,
        kids: customerInfo.kids
      });
      setTrayRecommendations(response.data.recommendations);
    } catch (error) {
      console.error('Failed to get tray recommendations:', error);
    }
  };

  const addToCart = async (menuItem, traySize, quantity, dayNumber = null, mealType = null) => {
    setAddingToCart(menuItem.id);
    
    // Use selected day/meal if not provided
    const finalDayNumber = dayNumber || selectedDayMeal.day;
    const finalMealType = mealType || selectedDayMeal.meal;
    
    // Simulate brief loading for better UX
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const price = menuItem.pricing[traySize];
    const totalPrice = price * quantity;
    
    const cartItem = {
      menu_item_id: menuItem.id,
      menu_item_name: menuItem.name,
      category: menuItem.category,
      tray_size: traySize,
      unit_of_measure: menuItem.unit_of_measure || 'Tray',
      quantity: quantity,
      unit_price: price,
      total_price: totalPrice,
      day_number: finalDayNumber,
      meal_type: finalMealType,
      notes: menuItem.notes
    };

    setCart(prevCart => [...prevCart, cartItem]);
    setAddingToCart(null);
    
    // Get day and meal display names
    const dayInfo = eventDays.find(day => day.day_number === finalDayNumber);
    const dayDisplay = dayInfo ? dayInfo.display_date : `Day ${finalDayNumber}`;
    const mealDisplay = finalMealType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    
    // Show success toast
    toast.success(`Added ${menuItem.name} to cart!`, {
      description: `${dayDisplay} - ${mealDisplay}: ${quantity}x ${traySize.replace('_', ' ')} - $${Math.floor(totalPrice)}`,
      duration: 3000,
    });
  };

  const removeFromCart = (index) => {
    setCart(prevCart => prevCart.filter((_, i) => i !== index));
  };

  const updateCartItemQuantity = (index, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(index);
      return;
    }
    
    setCart(prevCart => 
      prevCart.map((item, i) => 
        i === index 
          ? { 
              ...item, 
              quantity: newQuantity, 
              total_price: item.unit_price * newQuantity 
            }
          : item
      )
    );
    
    toast.success('Quantity updated!', {
      description: `Updated to ${newQuantity} items`,
      duration: 2000,
    });
  };
  
  const updateCartItemTraySize = async (index, newTraySize) => {
    const item = cart[index];
    if (!item) return;
    
    try {
      // Fetch the menu item to get the new price for the selected tray size
      const storeId = getStoreId();
      const menuItemResponse = await axios.get(`${API}/menu-items/${item.menu_item_id}${storeId ? `?store_id=${storeId}` : ''}`);
      const menuItem = menuItemResponse.data;
      
      if (menuItem && menuItem.pricing && menuItem.pricing[newTraySize]) {
        const newUnitPrice = menuItem.pricing[newTraySize];
        const newTotalPrice = newUnitPrice * item.quantity;
        
        setCart(prevCart => 
          prevCart.map((cartItem, i) => 
            i === index 
              ? { 
                  ...cartItem, 
                  tray_size: newTraySize,
                  unit_price: newUnitPrice,
                  total_price: newTotalPrice
                }
              : cartItem
          )
        );
        
        toast.success(`Updated to ${newTraySize.replace('_', ' ')}`);
      } else {
        toast.error('Price not available for this size');
      }
    } catch (error) {
      console.error('Failed to update tray size:', error);
      toast.error('Failed to update tray size');
    }
  };

  const updateCartItemPrice = (index, newUnitPrice) => {
    if (newUnitPrice < 0) return;
    
    setCart(prevCart => 
      prevCart.map((item, i) => 
        i === index 
          ? { 
              ...item, 
              unit_price: newUnitPrice, 
              total_price: newUnitPrice * item.quantity 
            }
          : item
      )
    );
    
    toast.success('Price updated!', {
      description: `New unit price: $${Math.floor(newUnitPrice)}`,
      duration: 2000,
    });
  };

  // Update cart item notes (e.g., "less spicy", "extra plates")
  const updateCartItemNotes = (index, notes) => {
    setCart(prevCart => 
      prevCart.map((item, i) => 
        i === index ? { ...item, notes: notes } : item
      )
    );
  };

  // Group cart items by day and meal for display
  const getCartByDayMeal = () => {
    const grouped = {};
    
    cart.forEach(item => {
      const dayKey = item.day_number || 1;
      const mealKey = item.meal_type || 'breakfast';
      
      if (!grouped[dayKey]) {
        grouped[dayKey] = {};
      }
      
      if (!grouped[dayKey][mealKey]) {
        grouped[dayKey][mealKey] = [];
      }
      
      grouped[dayKey][mealKey].push(item);
    });
    
    return grouped;
  };

  // Calculate totals by day and meal
  const calculateDayMealTotals = () => {
    const grouped = getCartByDayMeal();
    const totals = {};
    let grandTotal = 0;
    
    Object.keys(grouped).forEach(dayKey => {
      totals[dayKey] = {};
      let dayTotal = 0;
      
      Object.keys(grouped[dayKey]).forEach(mealKey => {
        const mealItems = grouped[dayKey][mealKey];
        const mealTotal = Math.floor(mealItems.reduce((sum, item) => sum + item.total_price, 0));
        totals[dayKey][mealKey] = mealTotal;
        dayTotal += mealTotal;
      });
      
      totals[dayKey].dayTotal = Math.floor(dayTotal);
      grandTotal += dayTotal;
    });
    
    totals.grandTotal = Math.floor(grandTotal);
    return totals;
  };

  const calculateSubtotal = () => {
    // Round down to remove decimals - catering doesn't charge loose change
    return Math.floor(cart.reduce((sum, item) => sum + item.total_price, 0));
  };

  const calculateServiceFee = () => {
    const subtotal = calculateSubtotal();
    if (serviceFeeType === 'none') return 0;
    if (serviceFeeType === 'custom') return Math.floor(customServiceFee);
    
    // Extract percentage from string like "10%" and calculate, then round down
    const percentage = parseFloat(serviceFeeType.replace('%', ''));
    return Math.floor((subtotal * percentage) / 100);
  };

  const calculateDiscountAmount = () => {
    const subtotal = calculateSubtotal();
    if (discountType === 'none' || !discountPercentage || discountPercentage === '') return 0;
    if (discountPercentage === 'custom') return Math.floor(customDiscountAmount);
    
    // Extract percentage from string like "10%" and calculate, then round down
    const percentage = parseFloat(discountPercentage.replace('%', ''));
    return Math.floor((subtotal * percentage) / 100);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const serviceFeeAmount = calculateServiceFee();
    const discountAmountCalc = calculateDiscountAmount();
    const total = subtotal + serviceFeeAmount - discountAmountCalc;
    // Final total is already from floored values, but ensure it's an integer
    return Math.max(0, Math.floor(total));
  };

  const calculateBalanceAmount = () => {
    const total = calculateTotal();
    const balance = total - Math.floor(advancePayment);
    return Math.max(0, Math.floor(balance));
  };

  // Helper function to check if address is required based on catering type
  const isAddressRequired = () => {
    return cateringType === 'delivery' || cateringType === 'delivery_setup' || cateringType === 'live';
  };

  // Generate event days when start/end dates are selected
  const generateEventDays = (startDate, endDate) => {
    if (!startDate || !endDate) return [];
    
    const days = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    let currentDate = new Date(start);
    let dayNumber = 1;
    
    while (currentDate <= end) {
      days.push({
        day_number: dayNumber,
        date: format(currentDate, 'yyyy-MM-dd'),
        display_date: format(currentDate, 'EEEE, MMM dd'),
        selected_meals: []
      });
      currentDate.setDate(currentDate.getDate() + 1);
      dayNumber++;
    }
    
    return days;
  };

  // Update customer info when dates change
  const updateEventDates = (startDate, endDate) => {
    // Always update the customer info with whatever dates we have
    setCustomerInfo(prev => ({
      ...prev,
      start_date: startDate || prev.start_date,
      end_date: endDate || prev.end_date
    }));
    
    // Only generate event days if we have both dates
    if (startDate && endDate) {
      const days = generateEventDays(startDate, endDate);
      
      // Preserve existing meal selections when editing
      // Map new days to old days by day_number and preserve meal data
      const preservedDays = days.map(newDay => {
        const existingDay = eventDays.find(d => d.day_number === newDay.day_number);
        if (existingDay && existingDay.selected_meals && existingDay.selected_meals.length > 0) {
          // Keep the existing meal selections and times
          return {
            ...newDay,
            selected_meals: existingDay.selected_meals,
            meal_times: existingDay.meal_times || []
          };
        }
        return newDay;
      });
      
      setEventDays(preservedDays);
      setCustomerInfo(prev => ({
        ...prev,
        start_date: startDate,
        end_date: endDate,
        day_meal_selections: preservedDays
      }));
    }
  };

  // Toggle meal selection for a specific day
  const toggleMealSelection = (dayNumber, mealType) => {
    const updatedDays = eventDays.map(day => {
      if (day.day_number === dayNumber) {
        const currentMeals = day.selected_meals || [];
        const mealExists = currentMeals.includes(mealType);
        
        let newMeals = mealExists 
          ? currentMeals.filter(meal => meal !== mealType)
          : [...currentMeals, mealType];
        
        // Set default times for new meals
        const updatedMealTimes = day.meal_times || [];
        if (!mealExists) {
          // Add default time for new meal
          const defaultTimes = {
            breakfast: '08:00',
            lunch: '12:00',
            evening_snacks: '16:00',
            dinner: '18:00'
          };
          
          updatedMealTimes.push({
            meal_type: mealType,
            delivery_time: defaultTimes[mealType] || '12:00'
          });
        } else {
          // Remove time for removed meal
          updatedMealTimes.splice(
            updatedMealTimes.findIndex(mt => mt.meal_type === mealType), 1
          );
        }
        
        return {
          ...day,
          selected_meals: newMeals,
          meal_times: updatedMealTimes
        };
      }
      return day;
    });
    
    setEventDays(updatedDays);
    setCustomerInfo(prev => ({
      ...prev,
      day_meal_selections: updatedDays
    }));
  };

  // Get meal time for a specific day and meal
  const getMealTime = (dayNumber, mealType) => {
    const day = eventDays.find(d => d.day_number === dayNumber);
    const mealTime = day?.meal_times?.find(mt => mt.meal_type === mealType);
    return mealTime?.delivery_time || '';
  };

  // Update meal time for a specific day and meal
  const updateMealTime = (dayNumber, mealType, time) => {
    const updatedDays = eventDays.map(day => {
      if (day.day_number === dayNumber) {
        const updatedMealTimes = day.meal_times || [];
        const existingIndex = updatedMealTimes.findIndex(mt => mt.meal_type === mealType);
        
        if (existingIndex >= 0) {
          updatedMealTimes[existingIndex].delivery_time = time;
        } else {
          updatedMealTimes.push({ meal_type: mealType, delivery_time: time });
        }
        
        return {
          ...day,
          meal_times: updatedMealTimes
        };
      }
      return day;
    });
    
    setEventDays(updatedDays);
    setCustomerInfo(prev => ({
      ...prev,
      day_meal_selections: updatedDays
    }));
  };

  // Custom item functions
  const fetchAllMenuItemsForDuplicateCheck = async () => {
    try {
      const storeId = getStoreId();
      const catResponse = await axios.get(`${API}/categories${storeId ? `?store_id=${storeId}` : ''}`);
      const allItems = [];
      for (const cat of catResponse.data) {
        const items = await axios.get(`${API}/menu-items?category=${cat}${storeId ? `&store_id=${storeId}` : ''}`);
        allItems.push(...items.data);
      }
      // Deduplicate
      const uniqueItems = [];
      const seenIds = new Set();
      for (const item of allItems) {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          uniqueItems.push(item);
        }
      }
      setAllMenuItemsForDuplicateCheck(uniqueItems);
    } catch (error) {
      console.error('Failed to fetch menu items for duplicate check:', error);
    }
  };

  const resetCustomItem = () => {
    setCustomItem({
      name: '',
      category: 'CUSTOM',
      protein_type: '',
      tray_size: 'small',
      quantity: 1,
      total_price: '',
      unit_of_measure: 'Tray',
      notes: '',
      customCategory: '',
      customUOM: '',
      customSize: '',
      pricing: { qt: '', mht: '', ht: '', mlt: '', lt: '', per_piece: '' },
      traySelection: {
        qt: { add: false, qty: 1 },
        mht: { add: false, qty: 1 },
        ht: { add: false, qty: 1 },
        mlt: { add: false, qty: 1 },
        lt: { add: false, qty: 1 },
      },
      pieceQty: 1,
      pieceAdd: true,
    });
    setCustomSizes([{ size: '', price: '', addToCart: false }]);
  };

  const addCustomItem = async () => {
    // For Custom UOM, use customSizes validation
    if (customItem.unit_of_measure === 'Custom') {
      const validSizes = customSizes.filter(s => s.size && s.price);
      const sizesToAddToCart = customSizes.filter(s => s.size && s.price && s.addToCart);
      
      if (!customItem.name || validSizes.length === 0) {
        toast.error('Please enter item name and at least one size with price');
        return;
      }
      
      if (sizesToAddToCart.length === 0) {
        toast.error('Please check at least one size to add to cart');
        return;
      }
      
      if (!selectedDayMeal) {
        toast.error('Please select a day and meal');
        return;
      }
      
      // Process custom UOM with multiple sizes
      const finalCategory = customItem.category === 'CUSTOM' && customItem.customCategory 
        ? customItem.customCategory 
        : (customItem.category || 'CUSTOM');
      
      const finalUOM = customItem.customUOM || 'Custom';
      
      // Build pricing object for all valid sizes
      const pricingObj = {};
      validSizes.forEach(sizeItem => {
        const sizeKey = sizeItem.size.toLowerCase().replace(/\s+/g, '_');
        pricingObj[sizeKey] = parseFloat(sizeItem.price);
      });
      
      // Save to database with all sizes
      try {
        const menuItemId = `custom-${Date.now()}`;
        await axios.post(`${API}/menu-items?store_id=${getStoreId()}`, {
          id: menuItemId,
          name: customItem.name,
          storeId: getStoreId(),
          category: finalCategory,
          protein_type: customItem.protein_type || 'custom',
          pricing: pricingObj,
          unit_of_measure: finalUOM,
          notes: customItem.notes || ''
        }, {
          headers: {
            'X-User-Role': currentUser?.role || 'store_admin'
          }
        });
        toast.success(`Custom item saved to menu with ${validSizes.length} size(s)!`);
        
        // Add checked sizes to cart
        const newCartItems = [];
        sizesToAddToCart.forEach(sizeItem => {
          const sizeKey = sizeItem.size.toLowerCase().replace(/\s+/g, '_');
          newCartItems.push({
            menu_item_id: menuItemId,
            menu_item_name: customItem.name,
            category: finalCategory,
            tray_size: sizeKey,
            unit_of_measure: finalUOM,
            quantity: 1,
            unit_price: parseFloat(sizeItem.price),
            total_price: parseFloat(sizeItem.price),
            day_number: selectedDayMeal.day,
            meal_type: selectedDayMeal.meal,
            notes: customItem.notes,
            is_custom: true
          });
        });
        
        setCart(prevCart => [...prevCart, ...newCartItems]);
        
        const dayInfo = eventDays.find(day => day.day_number === selectedDayMeal.day);
        const dayDisplay = dayInfo ? dayInfo.display_date : `Day ${selectedDayMeal.day}`;
        const mealDisplay = selectedDayMeal.meal.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
        
        toast.success(`Added ${sizesToAddToCart.length} size(s) to cart`, {
          description: `${dayDisplay} - ${mealDisplay}`,
          duration: 3000,
        });
      } catch (error) {
        console.error('Failed to save custom item to database:', error);
        console.error('Error details:', error.response?.data);
        toast.error('Failed to save item. Please try again.');
        return;
      }
      
      setShowCustomItemModal(false);
      resetCustomItem();
      return;
    }
    
    // ===== UOM-aware pricing parity with Menu Management =====
    if (!customItem.name) {
      toast.error('Please enter the item name.');
      return;
    }
    if (!selectedDayMeal) {
      toast.error('Please select a day and meal');
      return;
    }

    const uom = (customItem.unit_of_measure || '').toLowerCase();
    const isTrayUOM = uom.includes('tray');

    const finalCategory = customItem.category === 'CUSTOM' && customItem.customCategory
      ? customItem.customCategory
      : (customItem.category || 'CUSTOM');
    const finalUOM = customItem.unit_of_measure;

    // Build pricing object for menu_items + selected cart rows
    const pricingObj = {};
    const cartAdds = [];

    if (isTrayUOM) {
      const TRAY_KEYS = ['qt', 'mht', 'ht', 'mlt', 'lt'];
      TRAY_KEYS.forEach((k) => {
        const priceRaw = customItem.pricing?.[k];
        const price = parseFloat(priceRaw);
        if (!Number.isNaN(price) && price > 0) {
          pricingObj[k] = price;
          const sel = customItem.traySelection?.[k];
          if (sel?.add) {
            const qty = Math.max(1, parseInt(sel.qty) || 1);
            cartAdds.push({ sizeKey: k, qty, unitPrice: price });
          }
        }
      });
      if (Object.keys(pricingObj).length === 0) {
        toast.error('Please enter at least one tray-size price.');
        return;
      }
      if (cartAdds.length === 0) {
        toast.error('Please check at least one size to add to this order.');
        return;
      }
    } else {
      // Per Piece / Per Order / Per Person / other single-price UOMs
      const price = parseFloat(customItem.pricing?.per_piece);
      if (Number.isNaN(price) || price <= 0) {
        toast.error('Please enter a price.');
        return;
      }
      pricingObj.per_piece = price;
      if (customItem.pieceAdd !== false) {
        const qty = Math.max(1, parseInt(customItem.pieceQty) || 1);
        cartAdds.push({ sizeKey: 'per_piece', qty, unitPrice: price });
      }
      if (cartAdds.length === 0) {
        toast.error('Please check the size to add to this order, or use Menu Management to save without adding.');
        return;
      }
    }

    // Save full pricing object to menu_items (parity with Menu Management)
    const menuItemId = `custom-${Date.now()}`;
    try {
      await axios.post(`${API}/menu-items?store_id=${getStoreId()}`, {
        id: menuItemId,
        name: customItem.name,
        storeId: getStoreId(),
        category: finalCategory,
        protein_type: customItem.protein_type || 'custom',
        pricing: pricingObj,
        unit_of_measure: finalUOM,
        notes: customItem.notes || ''
      }, {
        headers: { 'X-User-Role': currentUser?.role || 'store_admin' }
      });
      toast.success('Custom item saved to menu!');
    } catch (error) {
      console.error('Failed to save custom item to database:', error);
      console.error('Error details:', error.response?.data);
      // Still continue to add to cart even if database save fails
    }

    // Add selected sizes to cart
    const newCartItems = cartAdds.map(({ sizeKey, qty, unitPrice }) => ({
      menu_item_id: menuItemId,
      menu_item_name: customItem.name,
      category: finalCategory,
      tray_size: sizeKey,
      unit_of_measure: finalUOM,
      quantity: qty,
      unit_price: unitPrice,
      total_price: unitPrice * qty,
      day_number: selectedDayMeal.day,
      meal_type: selectedDayMeal.meal,
      notes: customItem.notes,
      is_custom: true,
    }));
    setCart(prev => [...prev, ...newCartItems]);

    const dayInfo = eventDays.find(day => day.day_number === selectedDayMeal.day);
    const dayDisplay = dayInfo ? dayInfo.display_date : `Day ${selectedDayMeal.day}`;
    const mealDisplay = selectedDayMeal.meal.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    toast.success(`Added ${cartAdds.length} size${cartAdds.length > 1 ? 's' : ''} to cart`, {
      description: `${dayDisplay} - ${mealDisplay}`,
      duration: 3000,
    });

    setShowCustomItemModal(false);
    resetCustomItem();
  };

  const recordAdvancePayment = async (orderId) => {
    try {
      if (advancePayment <= 0) return;
      
      const paymentData = {
        advance_payment: advancePayment,
        payment_method: paymentMethod,
        notes: paymentNotes
      };

      await axios.post(`${API}/orders/${orderId}/advance-payment`, paymentData);
      
      toast.success('Advance payment recorded!', {
        description: `$${Math.floor(advancePayment)} paid via ${paymentMethod}`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to record advance payment:', error);
      toast.error('Failed to record advance payment. Please try again.');
    }
  };

  const handleNextStep = () => {
    if (currentStep === 1 && customerInfo.adults > 0) {
      getTrayRecommendations();
      // Fetch recommendations when moving to menu selection
      fetchRecommendedPackage();
      fetchPopularItems();
    }
    const nextStep = Math.min(currentStep + 1, 5);
    setCurrentStep(nextStep);
    
    // Track when user reaches Final Review (Step 5)
    if (nextStep === 5) {
      setHasVisitedFinalReview(true);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const fetchRecommendedPackage = async () => {
    try {
      if (customerInfo.occasion && customerInfo.adults > 0) {
        const totalPeople = customerInfo.adults + customerInfo.kids;
        const response = await axios.post(`${API}/recommendations/generate-package`, {
          occasion: customerInfo.occasion,
          people_count: totalPeople
        });
        setRecommendedPackages([response.data]);
      }
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    }
  };

  const fetchPopularItems = async () => {
    try {
      const response = await axios.get(`${API}/analytics/popular-items?occasion=${encodeURIComponent(customerInfo.occasion || '')}&days=30`);
      setPopularItems(response.data.popular_items || []);
    } catch (error) {
      console.error('Failed to fetch popular items:', error);
    }
  };

  const submitOrder = async () => {
    // Prevent duplicate submissions
    if (loading) {
      return;
    }
    
    console.log('Submit Order - editingOrderId:', editingOrderId);
    console.log('Submit Order - submittedOrderId:', submittedOrderId);
    console.log('Submit Order - isMultiDayOrder:', isMultiDayOrder);
    
    try {
      setLoading(true);
      
      // Format customer info with dates
      const formattedCustomerInfo = {
        ...customerInfo,
        // Ensure adults and kids are valid integers (required by backend)
        adults: parseInt(customerInfo.adults) || 1,
        kids: parseInt(customerInfo.kids) || 0,
        // Ensure lead_employee is a string
        lead_employee: String(customerInfo.lead_employee || ''),
        // Format dates properly
        start_date: customerInfo.start_date ? format(customerInfo.start_date, 'yyyy-MM-dd') : '',
        end_date: customerInfo.end_date ? format(customerInfo.end_date, 'yyyy-MM-dd') : '',
        day_meal_selections: eventDays.map(day => ({
          day_number: day.day_number,
          date: day.display_date,
          selected_meals: day.selected_meals,
          meal_times: day.meal_times || []
        }))
      };
      
      // Debug log the formatted customer info
      console.log('Formatted customerInfo:', {
        adults: formattedCustomerInfo.adults,
        kids: formattedCustomerInfo.kids,
        lead_employee: formattedCustomerInfo.lead_employee,
        start_date: formattedCustomerInfo.start_date,
        end_date: formattedCustomerInfo.end_date
      });

      let response;
      
      // Check if this is a multi-day order with sub-orders
      // Helper function to validate and format cart items
      const validateCartItems = (items) => items.map(item => ({
        menu_item_id: String(item.menu_item_id || item.id || ''),
        menu_item_name: String(item.menu_item_name || item.name || ''),
        category: String(item.category || ''),
        tray_size: String(item.tray_size || item.size || ''),
        quantity: parseInt(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        total_price: parseFloat(item.total_price) || 0,
        notes: String(item.notes || ''),
        day_number: item.day_number ? parseInt(item.day_number) : null,
        meal_type: item.meal_type || null,
        unit_of_measure: item.unit_of_measure || null
      }));

      if (isMultiDayOrder && !editingOrderId) {
        // Group cart items by day+meal
        const itemsByDayMeal = {};
        cart.forEach(item => {
          const key = `${item.day_number || 1}-${item.meal_type || 'breakfast'}`;
          if (!itemsByDayMeal[key]) {
            itemsByDayMeal[key] = [];
          }
          itemsByDayMeal[key].push(item);
        });
        
        // Build sub-orders array
        const subOrders = [];
        Object.keys(itemsByDayMeal).forEach(key => {
          const [dayNum, mealType] = key.split('-');
          const dayNumber = parseInt(dayNum);
          const items = itemsByDayMeal[key];
          const headcount = dayMealHeadcounts[key] || (parseInt(customerInfo.adults) || 1) + (parseInt(customerInfo.kids) || 0);
          
          // Calculate subtotal for this sub-order with validated items - round down to remove decimals
          const validatedItems = validateCartItems(items);
          const subtotal = Math.floor(validatedItems.reduce((sum, item) => sum + item.total_price, 0));
          
          subOrders.push({
            day_number: dayNumber,
            meal_type: mealType,
            headcount: parseInt(headcount) || 1,
            items: validatedItems,
            subtotal: subtotal,
            service_fee: 0, // Service fee applied at group level
            discount_amount: 0, // Discount applied at group level
            total_amount: subtotal
          });
        });
        
        // Create multi-order payload - all amounts already floored from calculate functions
        const multiOrderData = {
          storeId: getStoreId(),
          customer_info: formattedCustomerInfo,
          catering_type: cateringType,
          sub_orders: subOrders,
          group_service_fee: Math.floor(parseFloat(calculateServiceFee()) || 0),
          service_fee_description: serviceFeeDescription || '',
          group_discount_type: discountType === 'none' ? '' : discountType,
          group_discount_amount: Math.floor(parseFloat(calculateDiscountAmount()) || 0),
          group_total_amount: Math.floor(parseFloat(calculateTotal()) || 0),
          advance_payment: Math.floor(parseFloat(advancePayment) || 0),
          advance_payment_date: advancePayment > 0 ? new Date().toISOString() : '',
          balance_amount: Math.floor(parseFloat(calculateBalanceAmount()) || 0),
          status: orderStatus,
          notes: ''
        };
        
        console.log('Creating multi-day order with sub-orders:', subOrders.length);
        response = await axios.post(`${API}/orders/bulk-create`, multiOrderData);
        
        // Store the parent order ID for invoice generation
        setSubmittedOrderId(response.data.parent_order_id);
        
        toast.success(`${response.data.sub_orders.length} sub-orders created!`, {
          description: `Group ID: ${response.data.group_id.slice(0, 8)}... | Combined total: $${Math.floor(response.data.group_total)}`,
          duration: 4000,
        });
      } else {
        // Standard single order flow
        // Use the shared validation helper
        const validatedCart = validateCartItems(cart);

        // All amounts rounded down to remove decimals - catering doesn't charge loose change
        const orderData = {
          storeId: getStoreId(),
          customer_info: formattedCustomerInfo,
          catering_type: cateringType,
          items: validatedCart,
          service_fee: Math.floor(parseFloat(calculateServiceFee()) || 0),
          service_fee_description: serviceFeeDescription || '',
          subtotal: Math.floor(parseFloat(calculateSubtotal()) || 0),
          discount_type: discountType === 'none' ? '' : discountType,
          discount_amount: Math.floor(parseFloat(calculateDiscountAmount()) || 0),
          total_amount: Math.floor(parseFloat(calculateTotal()) || 0),
          advance_payment: Math.floor(parseFloat(advancePayment) || 0),
          advance_payment_date: advancePayment > 0 ? new Date().toISOString() : '',
          balance_amount: Math.floor(parseFloat(calculateBalanceAmount()) || 0),
          status: orderStatus,
          notes: '',
          updated_by: editingOrderId ? customerInfo.prepared_by : '',
          update_reason: editingOrderId ? updateReason : ''
        };

        if (editingOrderId) {
          // Update existing order
          console.log('Updating order:', editingOrderId);
          console.log('Order payload:', JSON.stringify(orderData, null, 2));
          console.log('Payload validation check:', {
            storeId: typeof orderData.storeId,
            adults: typeof orderData.customer_info?.adults,
            adultsValue: orderData.customer_info?.adults,
            kids: typeof orderData.customer_info?.kids,
            kidsValue: orderData.customer_info?.kids,
            itemsCount: orderData.items?.length,
            firstItemQuantity: orderData.items?.[0]?.quantity,
            firstItemQuantityType: typeof orderData.items?.[0]?.quantity
          });
          response = await axios.put(`${API}/orders/${editingOrderId}`, orderData);
          setSubmittedOrderId(editingOrderId);
          toast.success('Order updated successfully!', {
            description: `Order ID: ${editingOrderId.slice(0, 8)}... has been updated`,
            duration: 4000,
          });
        } else {
          // Create new order
          console.log('Creating new order');
          response = await axios.post(`${API}/orders`, orderData);
          setSubmittedOrderId(response.data.id);
          const eventDuration = eventDays.length > 1 ? `${eventDays.length}-day event` : 'single-day event';
          toast.success('Order submitted successfully!', {
            description: `Order ID: ${response.data.id.slice(0, 8)}... | ${eventDuration} ${advancePayment > 0 ? `| Advance: $${Math.floor(advancePayment)}` : ''}`,
            duration: 2000,
          });
        }
      }
      
      // Don't reset form immediately, show invoice options
    } catch (error) {
      console.error('Failed to submit order:', error);
      console.error('Error details:', error.response?.data);
      toast.error(`Failed to submit order: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (invoiceType = 'customer') => {
    try {
      if (!submittedOrderId) return;
      
      const response = await axios.post(
        `${API}/orders/${submittedOrderId}/generate-invoice?invoice_type=${invoiceType}`,
        {},
        { responseType: 'blob' }
      );
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `bawarchi_${invoiceType}_invoice_${submittedOrderId.slice(0, 8)}.pdf`; // fallback
      
      console.log('Content-Disposition header:', contentDisposition);
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          console.log('Extracted filename from header:', filename);
        }
      } else {
        console.log('No Content-Disposition header found, using fallback filename');
      }
      
      console.log('Final filename for download:', filename);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${invoiceType === 'customer' ? 'Customer' : 'Kitchen'} invoice downloaded!`);
    } catch (error) {
      console.error('Failed to download invoice:', error);
      toast.error('Failed to download invoice. Please try again.');
    }
  };

  const addRecommendedPackage = () => {
    if (recommendedPackages.length > 0) {
      const packageItems = recommendedPackages[0].package_items.map(item => ({
        menu_item_id: `rec-${Date.now()}-${Math.random()}`,
        menu_item_name: item.menu_item_name,
        category: item.category,
        tray_size: item.tray_size,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price,
        notes: `Popular choice #${item.popularity_rank}`
      }));
      
      setCart(prevCart => [...prevCart, ...packageItems]);
      toast.success('Recommended package added to cart!', {
        description: `${packageItems.length} items added based on popular choices`,
        duration: 3000,
      });
    }
  };

  // Navigation functions
  const startNewOrder = () => {
    setEditingOrderId(null);
    setViewingOrderId(null); // Clear viewing mode
    setCurrentView('orderForm');
    setCurrentStep(1);
    setHasVisitedFinalReview(false); // Reset final review flag
    // Reset all form data
    setCustomerInfo({
      name: '',
      phone: '',
      email: '',
      prepared_by: '',
      lead_employee: '',
      occasion: '',
      adults: 1,
      kids: 0,
      start_date: null,
      end_date: null,
      day_meal_selections: [],
      address: ''
    });
    setEventDays([]);
    setCart([]);
    setCateringType('');
    setCateringTypeDescription('');
    setServiceFee(0);
    setServiceFeeType('none');
    setServiceFeeDescription('');
    setCustomServiceFee(0);
    setDiscountType('none');
    setDiscountPercentage('');
    setCustomDiscountAmount(0);
    setAdvancePayment(0);
    setSubmittedOrderId(null);
    // Reset multi-day order states
    setIsMultiDayOrder(false);
    setDayMealHeadcounts({});
  };

  const copyOrderForNew = async (orderId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/orders/${orderId}`);
      const order = response.data;
      
      // Load customer info WITHOUT the order ID
      const customerData = order.customer_info;
      
      // Parse dates correctly to avoid timezone offset issues
      const parseLocalDate = (dateStr) => {
        if (!dateStr) return null;
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      };
      
      setCustomerInfo({
        ...customerData,
        start_date: customerData.start_date ? parseLocalDate(customerData.start_date) : null,
        end_date: customerData.end_date ? parseLocalDate(customerData.end_date) : null,
      });
      
      // Restore event days from saved data
      if (customerData.day_meal_selections && customerData.day_meal_selections.length > 0) {
        setEventDays(customerData.day_meal_selections);
      } else if (customerData.start_date && customerData.end_date) {
        const days = generateEventDays(
          parseLocalDate(customerData.start_date),
          parseLocalDate(customerData.end_date)
        );
        setEventDays(days);
      }
      
      // Load cart items
      setCart(order.items || []);
      
      // Load catering type
      setCateringType(order.catering_type);
      
      // Load pricing
      setServiceFee(order.service_fee || 0);
      setServiceFeeDescription(order.service_fee_description || '');
      setDiscountType(order.discount_type || 'none');
      setDiscountAmount(order.discount_amount || 0);
      
      // Set discount mode based on discount type
      if (order.discount_type === 'cash_discount') {
        setCustomDiscountAmount(order.discount_amount || 0);
      } else if (order.discount_amount > 0) {
        // Try to determine the percentage
        const subtotal = order.subtotal || 0;
        if (subtotal > 0) {
          const discountPercentageValue = ((order.discount_amount / subtotal) * 100).toFixed(0);
          if (['5', '10', '12', '15', '20', '25'].includes(discountPercentageValue)) {
            setDiscountPercentage(`${discountPercentageValue}%`);
          } else {
            setDiscountPercentage('custom');
            setCustomDiscountAmount(order.discount_amount);
          }
        }
      }
      
      setAdvancePayment(0); // Reset advance payment for new order
      setPaymentMethod('cash');
      setPaymentNotes('');
      setOrderStatus('quote'); // Always quote for new order
      
      setEditingOrderId(null); // NOT editing, creating new
      setCurrentView('orderForm');
      setCurrentStep(1);
      
      toast.success('Order copied! Ready to create as new order');
    } catch (error) {
      console.error('Failed to copy order:', error);
      toast.error('Failed to copy order');
    } finally {
      setLoading(false);
    }
  };

  const loadOrderForEditing = async (orderId) => {
    try {
      setLoading(true);
      setSubmittedOrderId(null); // Clear any previous submission confirmation
      const response = await axios.get(`${API}/orders/${orderId}`);
      const order = response.data;
      
      // Load customer info
      const customerData = order.customer_info;
      
      // Parse dates correctly to avoid timezone offset issues
      // Use date string directly as "YYYY-MM-DD" and create local date
      const parseLocalDate = (dateStr) => {
        if (!dateStr) return null;
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day); // Month is 0-indexed
      };
      
      setCustomerInfo({
        ...customerData,
        // Ensure adults and kids are valid numbers (required by backend)
        adults: parseInt(customerData.adults) || 1,
        kids: parseInt(customerData.kids) || 0,
        start_date: customerData.start_date ? parseLocalDate(customerData.start_date) : null,
        end_date: customerData.end_date ? parseLocalDate(customerData.end_date) : null,
        prepared_by: '' // Reset prepared_by so user inputs their name for the edit
      });
      
      // Restore event days from saved data (don't regenerate to preserve meal selections)
      if (customerData.day_meal_selections && customerData.day_meal_selections.length > 0) {
        setEventDays(customerData.day_meal_selections);
      } else if (customerData.start_date && customerData.end_date) {
        // Fallback: regenerate if meal selections not saved (backward compatibility)
        const days = generateEventDays(
          parseLocalDate(customerData.start_date),
          parseLocalDate(customerData.end_date)
        );
        setEventDays(days);
      }
      
      // Load cart items
      setCart(order.items || []);
      
      // Load catering type
      setCateringType(order.catering_type || '');
      setCateringTypeDescription(order.catering_type_description || '');
      
      // Load pricing - Service Fee
      setServiceFee(order.service_fee || 0);
      setServiceFeeDescription(order.service_fee_description || '');
      // Determine service fee type based on the value
      if (order.service_fee > 0) {
        const subtotal = order.subtotal || 0;
        if (subtotal > 0) {
          const feePercentage = ((order.service_fee / subtotal) * 100).toFixed(0);
          if (feePercentage === '5') setServiceFeeType('5%');
          else if (feePercentage === '10') setServiceFeeType('10%');
          else if (feePercentage === '12') setServiceFeeType('12%');
          else if (feePercentage === '15') setServiceFeeType('15%');
          else {
            setServiceFeeType('custom');
            setCustomServiceFee(order.service_fee);
          }
        } else {
          setServiceFeeType('custom');
          setCustomServiceFee(order.service_fee);
        }
      } else {
        setServiceFeeType('none');
        setCustomServiceFee(0);
      }
      
      // Load pricing - Discount
      console.log('📊 Loading order discount:', {
        type: order.discount_type,
        amount: order.discount_amount,
        subtotal: order.subtotal
      });
      setDiscountAmount(order.discount_amount || 0);
      if (order.discount_type && order.discount_type !== 'none' && order.discount_type !== '') {
        setDiscountType(order.discount_type);
        
        // Handle cash_discount (fixed amount)
        if (order.discount_type === 'cash_discount') {
          setCustomDiscountAmount(order.discount_amount || 0);
          setDiscountPercentage('custom'); // Set to 'custom' so the amount field shows
          console.log('✅ Loaded cash discount:', order.discount_amount);
        } 
        // Handle percentage-based discounts
        else if (order.discount_amount > 0) {
          const subtotal = order.subtotal || 0;
          if (subtotal > 0) {
            const discountPercentageValue = ((order.discount_amount / subtotal) * 100).toFixed(0);
            if (['5', '10', '12', '15', '20', '25'].includes(discountPercentageValue)) {
              setDiscountPercentage(`${discountPercentageValue}%`);
              setCustomDiscountAmount(0);
            } else {
              setDiscountPercentage('custom');
              setCustomDiscountAmount(order.discount_amount);
            }
          } else {
            setDiscountPercentage('custom');
            setCustomDiscountAmount(order.discount_amount);
          }
        }
      } else {
        setDiscountType('none');
        setDiscountPercentage('');
        setCustomDiscountAmount(0);
      }
      
      // Load payment info
      setAdvancePayment(order.advance_payment || 0);
      setPaymentMethod(order.payment_method || 'cash');
      setPaymentNotes(order.payment_notes || '');
      setOrderStatus(order.status);
      setUpdateReason(''); // Clear update reason for new edit (will be filled by user)
      
      setEditingOrderId(orderId);
      setCurrentView('orderForm');
      setCurrentStep(1);
      
      toast.success('Order loaded for editing');
    } catch (error) {
      console.error('Failed to load order:', error);
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const loadOrderForViewing = async (orderId, ordersList = null, orderIndex = null) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/orders/${orderId}`);
      const order = response.data;
      
      setPreviousView(currentView); // Save where we're coming from
      setViewingOrderId(order); // Store full order object for display
      
      // Set up navigation if orders list provided
      if (ordersList && ordersList.length > 0) {
        setViewingOrdersList(ordersList);
        setViewingOrderIndex(orderIndex !== null ? orderIndex : 0);
      } else {
        setViewingOrdersList([]);
        setViewingOrderIndex(0);
      }
      
      setCurrentView('viewOrder'); // New view type
      
      toast.success('Order loaded');
    } catch (error) {
      console.error('Failed to load order:', error);
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to previous/next order in viewing list
  const navigateToPrevOrder = async () => {
    if (viewingOrderIndex > 0) {
      const newIndex = viewingOrderIndex - 1;
      const prevOrder = viewingOrdersList[newIndex];
      if (prevOrder?.id) {
        const response = await axios.get(`${API}/orders/${prevOrder.id}`);
        setViewingOrderId(response.data);
        setViewingOrderIndex(newIndex);
      }
    }
  };

  const navigateToNextOrder = async () => {
    if (viewingOrderIndex < viewingOrdersList.length - 1) {
      const newIndex = viewingOrderIndex + 1;
      const nextOrder = viewingOrdersList[newIndex];
      if (nextOrder?.id) {
        const response = await axios.get(`${API}/orders/${nextOrder.id}`);
        setViewingOrderId(response.data);
        setViewingOrderIndex(newIndex);
      }
    }
  };

  // Handle login (new multi-tenant version)
  const handleLogin = async (user, token) => {
    try {
      // Save to localStorage
      localStorage.setItem('currentUser', JSON.stringify(user));
      localStorage.setItem('sessionToken', token);
      
      // Update state
      setCurrentUser(user);
      setSessionToken(token);
      
      // Determine role with proper differentiation
      if (user.role === 'super_admin') {
        setUserRole('super_admin');
      } else if (user.role === 'store_owner') {
        setUserRole('store_owner');
        
        // Fetch store details for store owner
        if (user.storeId) {
          const storeResponse = await axios.get(`${API}/stores/${user.storeId}`);
          setCurrentStore(storeResponse.data);
          localStorage.setItem('currentStore', JSON.stringify(storeResponse.data));
        }
      } else if (user.role === 'store_admin') {
        setUserRole('store_admin');
        
        // Fetch store details for store admin (manager)
        if (user.storeId) {
          const storeResponse = await axios.get(`${API}/stores/${user.storeId}`);
          setCurrentStore(storeResponse.data);
          localStorage.setItem('currentStore', JSON.stringify(storeResponse.data));
        }
      } else if (user.role === 'store_staff') {
        setUserRole('store_staff');
        
        console.log('=== STAFF LOGIN DEBUG ===');
        console.log('Staff user object:', user);
        console.log('Staff storeId:', user.storeId);
        
        // Fetch store details for staff
        if (user.storeId) {
          const storeResponse = await axios.get(`${API}/stores/${user.storeId}`);
          console.log('Fetched store for staff:', storeResponse.data);
          setCurrentStore(storeResponse.data);
          localStorage.setItem('currentStore', JSON.stringify(storeResponse.data));
        } else {
          console.error('WARNING: Staff user has no storeId!');
        }
      } else {
        setUserRole('user');
      }
      
      // Check if user needs to change password (first login with temp password)
      // Only show for users created by super admin with temp passwords
      // Users who signed up themselves (via /stores/signup) should not see this
      if (user.password_changed === false && user.role === 'store_owner' && user.created_by_admin === true) {
        setForcePasswordChange(true);
      }
      
      // Show welcome hint for first-time self-signup users
      if (user.role === 'store_owner' && user.created_by_admin === false && !user.last_login) {
        setShowWelcomeHint(true);
      }
      
      // Close inline sign-in
      setShowInlineSignIn(false);
    } catch (error) {
      console.error('Failed to complete login:', error);
      toast.error('Failed to load store details');
    }
  };
  
  // Handle logout (new multi-tenant version)
  const handleLogout = () => {
    // Clear all auth data including impersonation
    localStorage.removeItem('currentUser');
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('currentStore');
    localStorage.removeItem('originalUser');
    setOriginalUser(null);
    setCurrentUser(null);
    setSessionToken(null);
    setCurrentStore(null);
    setUserRole(null);
    setCurrentView('landing');
    setEditingOrderId(null);
    
    // Show inline sign-in panel on landing page
    setPublicView('landing');
    setShowInlineSignIn(true);
    
    toast.info('You have been logged out');
    setCurrentStep(1);
    toast.success('Logged out successfully');
  };

  // New login function (email or username / password)
  const handleEmailLogin = async (login, password) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/login`, { login, password });
      
      const { user, token } = response.data;
      
      // Save to state and localStorage
      setCurrentUser(user);
      setSessionToken(token);
      localStorage.setItem('currentUser', JSON.stringify(user));
      localStorage.setItem('sessionToken', token);
      
      // If user has storeId, fetch and save store info
      if (user.storeId) {
        try {
          const storeResponse = await axios.get(`${API}/stores/${user.storeId}`);
          setCurrentStore(storeResponse.data);
          localStorage.setItem('currentStore', JSON.stringify(storeResponse.data));
        } catch (error) {
          console.error('Failed to fetch store:', error);
        }
      }
      
      // Set userRole with proper differentiation (this triggers navigation to dashboard)
      if (user.role === 'super_admin') {
        setUserRole('super_admin');
      } else if (user.role === 'store_admin') {
        setUserRole('store_admin');
      } else if (user.role === 'store_staff') {
        setUserRole('store_staff');
      } else {
        setUserRole('user');
      }
      
      // Fetch subscription status for store users
      if (user.storeId) {
        await fetchSubscriptionStatus();
      }
      
      toast.success(`Welcome, ${user.name}!`);
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Legacy functions (kept for backward compatibility)
  const handleRoleSelect = (role) => {
    setUserRole(role);
  };

  const handleAdminLogin = () => {
    setUserRole('admin');
    toast.success('Logged in as Admin');
  };

  const handlePasswordChange = (newPassword) => {
    setAdminPassword(newPassword);
  };


  // Handle subscription plan selection
  const handlePlanSelect = async (planId) => {
    try {
      const response = await axios.post(`${API}/subscription/create-checkout`, {
        plan_id: planId,
        frontend_url: window.location.origin
      }, {
        headers: {
          'X-User-Email': currentUser?.email,
          'X-User-Role': userRole
        }
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
      toast.error('Failed to start upgrade process. Please try again.');
    }
  };

  // Handle billing cycle selection from payment modal
  const handleBillingCycleSelected = async (billingCycle) => {
    if (!pendingSignupData) {
      toast.error('Session expired. Please try again.');
      setShowPaymentModal(false);
      return;
    }

    const { user, token, planId } = pendingSignupData;
    
    // Extract plan tier (base or premium) and construct full plan_id
    const planTier = planId.includes('base') ? 'base' : 'premium';
    const fullPlanId = `${planTier}_${billingCycle}`;
    
    console.log('💳 Creating checkout session:', { fullPlanId, userEmail: user.email });
    
    try {
      toast.info('Redirecting to secure payment...');
      
      const response = await axios.post(`${API}/subscription/create-checkout`, {
        plan_id: fullPlanId,
        frontend_url: window.location.origin
      }, {
        headers: {
          'x-user-email': user.email
        }
      });
      
      console.log('✅ Checkout session created:', response.data);
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Failed to create checkout after signup:', error);
      toast.error('Payment initialization failed. Please try again.');
      setShowPaymentModal(false);
      setPendingSignupData(null);
    }
  };

  // Handle paid plan checkout after signup (legacy - keeping for compatibility)
  const handlePaidPlanCheckout = async (user, token, planId) => {
    try {
      toast.info('Redirecting to payment...');
      
      const response = await axios.post(`${API}/subscription/create-checkout`, {
        plan_id: planId,
        frontend_url: window.location.origin
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Failed to create checkout after signup:', error);
      toast.error('Account created but payment failed. Please try upgrading from your dashboard.');
      // Log them in anyway so they can access their account
      handleLogin(user, token);
    }
  };


  const handleUserPasswordChange = (newPassword) => {
    setUserPassword(newPassword);
  };

  const handleUserLogin = () => {
    setUserRole('user');
  };



  // Check for subscription success/cancel URLs
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('session_id');
  
  if (window.location.pathname === '/subscription/success' && sessionId) {
    return <SubscriptionSuccessPage 
      sessionId={sessionId}
      onContinue={() => {
        // Navigate to signin page
        window.location.href = '/?signin=true';
      }}
    />;
  }
  
  if (window.location.pathname === '/subscription/cancel') {
    return <SubscriptionCancelPage
      onRetry={() => setShowUpgradeModal(true)}
      onContinue={() => {
        window.history.pushState({}, '', '/');
        window.location.reload();
      }}
    />;
  }

  // Public feedback page - accessible without authentication
  const feedbackMatch = window.location.pathname.match(/^\/feedback\/([a-zA-Z0-9-]+)$/);
  if (feedbackMatch) {
    const orderId = feedbackMatch[1];
    return <FeedbackPage orderId={orderId} />;
  }

  // Render Payment Selection Modal if needed (must be before any early returns)
  console.log('🔍 Modal Check:', { showPaymentModal, hasPendingData: !!pendingSignupData });
  
  if (showPaymentModal && pendingSignupData) {
    console.log('🎯 RENDERING PAYMENT MODAL!', { planId: pendingSignupData.planId });
    return (
      <div>
        <PaymentSelectionModal 
          isOpen={showPaymentModal}
          planTier={pendingSignupData.planId?.includes('premium') ? 'premium' : 'base'}
          onSelectBillingCycle={handleBillingCycleSelected}
          onCancel={() => {
            // For paid plans, cannot cancel - modal is blocking
            toast.warning('Please complete payment to access your account');
          }}
        />
      </div>
    );
  }

  // Show public landing page if no user logged in
  if (!userRole) {
    if (publicView === 'landing') {
      return <PublicLandingPage 
        onSignIn={(user, token) => {
          // If called with user and token (from inline sign-in), handle login
          if (user && token) {
            handleLogin(user, token);
          } else {
            // Otherwise, show inline sign-in panel
            setShowInlineSignIn(true);
          }
        }}
        onSignUp={(planId = null) => {
          setSelectedPlan(planId);
          setPublicView('signup');
        }}
        showInlineSignIn={showInlineSignIn}
        onCloseInlineSignIn={() => setShowInlineSignIn(false)}
        onForgotPassword={() => setPublicView('forgotPassword')}
      />;
    } else if (publicView === 'signin') {
      return <RoleSelection 
        onSelectRole={handleRoleSelect} 
        onAdminLogin={handleAdminLogin}
        onUserLogin={handleUserLogin}
        onEmailLogin={handleEmailLogin}
        adminPassword={adminPassword}
        userPassword={userPassword}
        onBack={() => setPublicView('landing')}
      />;
    } else if (publicView === 'signup') {
      return <SignUpPage 
        onBack={() => {
          setSelectedPlan(null);
          setPublicView('landing');
        }}
        onSuccess={(user, token) => {
          // Check if this is a paid plan (base or premium)
          const isPaidPlan = selectedPlan && (selectedPlan.includes('base') || selectedPlan.includes('premium'));
          
          console.log('🔍 Signup Success - Plan Check:', { selectedPlan, isPaidPlan, hasToken: !!token });
          
          if (isPaidPlan && token) {
            // Store the signup data and show payment selection modal
            console.log('💳 Setting payment modal data...', { user: user.email, planId: selectedPlan });
            setPendingSignupData({ user, token, planId: selectedPlan });
            setShowPaymentModal(true);
            console.log('✅ Payment modal should now show');
          } else {
            // Free trial - just go to sign-in
            console.log('📝 Free trial - redirecting to signin');
            setSelectedPlan(null);
            setPublicView('signin');
          }
        }}
        selectedPlan={selectedPlan}
      />;
    } else if (publicView === 'forgotPassword') {
      return <ForgotPasswordPage 
        onBack={() => {
          setPublicView('landing');
          setShowInlineSignIn(true);
        }}
        onSuccess={() => setPublicView('landing')}
      />;
    } else if (publicView === 'resetPassword') {
      return <ResetPasswordPage 
        onBack={() => setPublicView('landing')}
        onSuccess={() => {
          setPublicView('landing');
          setShowInlineSignIn(true);
          // Store flag to show sign-in panel
          localStorage.setItem('showSignInPanel', 'true');
        }}
      />;
    }
  }

  // Force password change modal for first login
  if (forcePasswordChange && currentUser) {
    return <ForcedPasswordChangeModal 
      currentUser={currentUser}
      onSuccess={() => {
        setForcePasswordChange(false);
        // Update user object to reflect password change
        const updatedUser = { ...currentUser, password_changed: true };
        setCurrentUser(updatedUser);
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        toast.success('Password changed successfully! Welcome to CaterLog.');
      }}
    />;
  }

  // Show appropriate landing page based on role
  if (currentView === 'landing') {
    if (userRole === 'super_admin') {
      // Super Admin sees store management portal
      return (
        <>
          <ImpersonationBanner originalUser={originalUser} impersonatedUser={originalUser ? currentUser : null} onStopImpersonation={handleStopImpersonation} />
          <SuperAdminPortal 
            currentUser={currentUser}
            onLogout={handleLogout}
            onImpersonate={() => setShowImpersonationModal(true)}
            onViewAuditLogs={() => setCurrentView('auditLogs')}
          />
          <ImpersonationModal isOpen={showImpersonationModal} onClose={() => setShowImpersonationModal(false)} onImpersonate={handleStartImpersonation} />
          <UpgradeModal
            key="upgrade-modal-landing"
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            subscriptionStatus={subscriptionStatus}
            onPlanSelect={handlePlanSelect}
            currentUser={currentUser}
          />
        </>
      );
    } else if (userRole === 'store_owner') {
      // Store Owner - Full Admin Access
      return (
        <>
          <ImpersonationBanner originalUser={originalUser} impersonatedUser={originalUser ? currentUser : null} onStopImpersonation={handleStopImpersonation} />
          <LandingPage 
            onStartNew={startNewOrder} 
            onViewReports={() => setCurrentView('reports')}
            onViewMenuManagement={() => setCurrentView('menuManagement')}
            onViewTeamManagement={() => setCurrentView('teamManagement')}
            onViewStoreSetup={() => setCurrentView('storeSetup')}
            onViewAuditLogs={() => setCurrentView('auditLogs')}
            onViewCustomerManagement={() => setCurrentView('customerManagement')}
            onEditOrder={loadOrderForEditing}
            onViewOrder={loadOrderForViewing}
            onPasswordChange={{ currentPassword: adminPassword, onChange: setAdminPassword }}
            onUserPasswordChange={{ currentPassword: userPassword, onChange: setUserPassword }}
            onLogout={handleLogout}
            onCopyOrder={copyOrderForNew}
            currentUser={currentUser}
            currentStore={currentStore}
            subscriptionStatus={subscriptionStatus}
            onUpgrade={() => setShowUpgradeModal(true)}
            onManageSubscription={() => setShowManageSubscriptionModal(true)}
          />
          <UpgradeModal
            key="upgrade-modal-landing"
            isOpen={showUpgradeModal}
            onClose={() => {
              setShowUpgradeModal(false);
              // If opened from manage subscription, reopen that modal
              if (upgradeModalSource === 'manageSubscription') {
                setShowManageSubscriptionModal(true);
                setUpgradeModalSource(null);
              }
            }}
            subscriptionStatus={subscriptionStatus}
            onPlanSelect={handlePlanSelect}
            currentUser={currentUser}
          />
          <ManageSubscriptionModal
            isOpen={showManageSubscriptionModal}
            onClose={() => setShowManageSubscriptionModal(false)}
            currentUser={currentUser}
            currentStore={currentStore}
          />
        </>
      );
    } else if (userRole === 'store_admin' || userRole === 'store_staff') {
      // Store Admin (Manager) - Staff view + edit confirmed orders
      // Store Staff - Staff view only
      return (
        <>
          <ImpersonationBanner originalUser={originalUser} impersonatedUser={originalUser ? currentUser : null} onStopImpersonation={handleStopImpersonation} />
          <UserLandingPage 
            onStartNew={startNewOrder} 
            onEditOrder={loadOrderForEditing}
            onViewOrder={loadOrderForViewing}
            onCopyOrder={copyOrderForNew}
            onLogout={handleLogout}
            currentStore={currentStore}
            currentUser={currentUser}
            subscriptionStatus={subscriptionStatus}
            onUpgrade={() => setShowUpgradeModal(true)}
            canEditConfirmedOrders={userRole === 'store_admin'}
          />
          <UpgradeModal
            key="upgrade-modal-landing"
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            subscriptionStatus={subscriptionStatus}
            onPlanSelect={handlePlanSelect}
            currentUser={currentUser}
          />
        </>
      );
    } else {
      return <UserLandingPage 
        onStartNew={startNewOrder}
        onEditOrder={loadOrderForEditing}
        onViewOrder={loadOrderForViewing}
        onCopyOrder={copyOrderForNew}
        onLogout={handleLogout}
        currentStore={currentStore}
        canEditConfirmedOrders={false}
      />;
    }
  }

  // Show reports page if on reports view (Owner only)
  if (currentView === 'reports') {
    if (userRole !== 'store_owner') {
      toast.error('Access denied. Owner only.');
      setCurrentView('landing');
      return null;
    }
    return (
      <>
        <ImpersonationBanner originalUser={originalUser} impersonatedUser={originalUser ? currentUser : null} onStopImpersonation={handleStopImpersonation} />
        <TrialBanner subscriptionStatus={subscriptionStatus} onUpgrade={() => setShowUpgradeModal(true)} />
        <ReportsPage onBack={() => setCurrentView('landing')} onViewOrder={loadOrderForViewing} currentStore={currentStore} />
        <UpgradeModal
          key="upgrade-modal-reports"
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          subscriptionStatus={subscriptionStatus}
          onPlanSelect={handlePlanSelect}
          currentUser={currentUser}
        />
      </>
    );
  }

  // Show menu management page if on menuManagement view (Owner only)
  if (currentView === 'menuManagement') {
    if (userRole !== 'store_owner') {
      toast.error('Access denied. Owner only.');
      setCurrentView('landing');
      return null;
    }
    return (
      <>
        <ImpersonationBanner originalUser={originalUser} impersonatedUser={originalUser ? currentUser : null} onStopImpersonation={handleStopImpersonation} />
        <TrialBanner subscriptionStatus={subscriptionStatus} onUpgrade={() => setShowUpgradeModal(true)} />
        <MenuManagementPage onBack={() => setCurrentView('landing')} currentStore={currentStore} currentUser={currentUser} />
        <UpgradeModal
          key="upgrade-modal-menu"
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          subscriptionStatus={subscriptionStatus}
          onPlanSelect={handlePlanSelect}
          currentUser={currentUser}
        />
      </>
    );
  }

  // Show team management page (Owner only)
  if (currentView === 'teamManagement') {
    if (userRole !== 'store_owner') {
      toast.error('Access denied. Owner only.');
      setCurrentView('landing');
      return null;
    }
    return (
      <>
        <ImpersonationBanner originalUser={originalUser} impersonatedUser={originalUser ? currentUser : null} onStopImpersonation={handleStopImpersonation} />
        <TrialBanner subscriptionStatus={subscriptionStatus} onUpgrade={() => setShowUpgradeModal(true)} />
        <TeamManagementPage 
          currentUser={currentUser}
          currentStore={currentStore}
          onBack={() => setCurrentView('landing')} 
        />
        <UpgradeModal
          key="upgrade-modal-team"
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          subscriptionStatus={subscriptionStatus}
          onPlanSelect={handlePlanSelect}
          currentUser={currentUser}
        />
      </>
    );
  }

  // Show store setup page (Owner only)
  if (currentView === 'storeSetup') {
    if (userRole !== 'store_owner') {
      toast.error('Access denied. Owner only.');
      setCurrentView('landing');
      return null;
    }
    return (
      <>
        <ImpersonationBanner originalUser={originalUser} impersonatedUser={originalUser ? currentUser : null} onStopImpersonation={handleStopImpersonation} />
        <TrialBanner subscriptionStatus={subscriptionStatus} onUpgrade={() => setShowUpgradeModal(true)} />
        <StoreSetupPage 
          currentUser={currentUser}
          currentStore={currentStore}
          onBack={() => setCurrentView('landing')}
          onStoreUpdate={(updatedStore) => {
            setCurrentStore(updatedStore);
            localStorage.setItem('currentStore', JSON.stringify(updatedStore));
          }}
        />
        <UpgradeModal
          key="upgrade-modal-setup"
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          subscriptionStatus={subscriptionStatus}
          onPlanSelect={handlePlanSelect}
          currentUser={currentUser}
        />
      </>
    );
  }

  // Show audit logs page (Owner + Super Admin)
  if (currentView === 'auditLogs') {
    if (userRole !== 'store_owner' && userRole !== 'super_admin') {
      toast.error('Access denied.');
      setCurrentView('landing');
      return null;
    }
    return (
      <>
        <ImpersonationBanner originalUser={originalUser} impersonatedUser={originalUser ? currentUser : null} onStopImpersonation={handleStopImpersonation} />
        <AuditLogPage
          currentUser={currentUser}
          onBack={() => setCurrentView('landing')}
        />
      </>
    );
  }

  // Show Customer Management page
  if (currentView === 'customerManagement') {
    // Handler to create order pre-filled with customer info
    const handleCreateOrderForCustomer = (customer) => {
      // Pre-fill customer info and navigate to order form
      setCustomerInfo(prev => ({
        ...prev,
        name: customer.name || '',
        phone: customer.phone || '',
        email: customer.email || '',
        address: customer.address || ''
      }));
      setEditingOrderId(null);
      setViewingOrderId(null);
      setPreviousView('customerManagement'); // Track where we're coming from
      setCurrentView('orderForm');
      setCurrentStep(1);
      toast.success(`Creating order for ${customer.name}`);
    };

    return (
      <>
        <ImpersonationBanner originalUser={originalUser} impersonatedUser={originalUser ? currentUser : null} onStopImpersonation={handleStopImpersonation} />
        <CustomerManagementPage
          onBack={() => setCurrentView('landing')}
          onCreateOrderForCustomer={handleCreateOrderForCustomer}
          currentStore={currentStore}
        />
        <Toaster position="bottom-right" richColors offset="80px" />
      </>
    );
  }

  // Show simple order view
  if (currentView === 'viewOrder' && viewingOrderId) {
    return (
      <>
        <ImpersonationBanner originalUser={originalUser} impersonatedUser={originalUser ? currentUser : null} onStopImpersonation={handleStopImpersonation} />
        <OrderView 
          order={viewingOrderId}
          currentStore={currentStore}
          onClose={() => {
            setViewingOrderId(null);
            setViewingOrdersList([]);
            setViewingOrderIndex(0);
            setCurrentView(previousView); // Return to where we came from
          }}
          onNavigatePrev={navigateToPrevOrder}
          onNavigateNext={navigateToNextOrder}
          currentIndex={viewingOrderIndex}
          totalOrders={viewingOrdersList.length}
        />
      </>
    );
  }

  if (!menuInitialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mb-4"></div>
            <p className="text-lg font-medium text-gray-700">Loading menu data...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Otherwise show order form
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bawarchi-background-muted)' }}>
      <ImpersonationBanner originalUser={originalUser} impersonatedUser={originalUser ? currentUser : null} onStopImpersonation={handleStopImpersonation} />
      {/* Trial Banner */}
      
      {/* Navigation */}
      <div className="nav-bawarchi">
        <div className="nav-brand flex items-center gap-2">
          {currentStore?.logoUrl && (
            <img src={getImageUrl(currentStore.logoUrl)} alt="Store Logo" className="h-16 w-auto" />
          )}
          <div className="flex flex-col">
            <span className="font-semibold">{currentStore?.name || 'CaterLog'}</span>
            {currentStore?.tagline && (
              <span className="text-sm text-gray-600">{currentStore.tagline}</span>
            )}
          </div>
        </div>
        {submittedOrderId ? (
          <button
            onClick={() => {
              // Reset all order state and navigate to dashboard
              setSubmittedOrderId(null);
              setCurrentStep(1);
              setCart([]);
              setCustomerInfo({
                name: '',
                phone: '',
                email: '',
                prepared_by: '',
                occasion: '',
                adults: 1,
                kids: 0,
                start_date: null,
                end_date: null,
                day_meal_selections: [],
                address: ''
              });
              setEventDays([]);
              setServiceFeeDescription('');
              setMenuSearchQuery('');
              setCateringType('');
              setServiceFee(0);
              setServiceFeeType('none');
              setCustomServiceFee(0);
              setDiscountType('none');
              setDiscountPercentage('');
              setDiscountAmount(0);
              setCustomDiscountAmount(0);
              setAdvancePayment(0);
              setPaymentMethod('cash');
              setPaymentNotes('');
              setOrderStatus('quote');
              setUpdateReason('');
              setMenuItems([]);
              setSelectedCategory('');
              setRecommendedPackages([]);
              setPopularItems([]);
              setDatePickerOpen(false);
              setTimePickerOpen(false);
              setCurrentView('landing');
              setEditingOrderId(null);
              setViewingOrderId(null);
            }}
            className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2"
            style={{ padding: '8px 20px', minHeight: '40px', borderRadius: '6px' }}
          >
            <Home className="w-4 h-4 mr-2 inline" />
            Back to Dashboard
          </button>
        ) : (
          <button
            onClick={() => {
              setCurrentView(previousView || 'landing');
              setEditingOrderId(null);
              setViewingOrderId(null);
            }}
            className="btn-bawarchi-secondary"
            style={{ padding: '8px 20px', minHeight: '40px' }}
          >
            <X className="w-4 h-4 mr-2 inline" />
            Cancel
          </button>
        )}
      </div>

      <div className="page-container">
        <div className="page-header text-center">
          <h1 className="page-title">
            {editingOrderId ? 'Edit Order' : 'Create New Order'}
          </h1>
          <p className="page-subtitle">Step-by-step order management</p>
        </div>

        {/* Progress Stepper */}
        <div className="stepper-bawarchi section-spacing">
          {[
            { num: 1, label: "Customer Info", icon: User },
            { num: 2, label: "Menu Selection", icon: ShoppingCart },
            { num: 3, label: "Review Cart", icon: CheckCircle },
            { num: 4, label: "Pricing & Fees", icon: CreditCard },
            { num: 5, label: "Final Review", icon: CheckCircle }
          ].map(({ num, label, icon: Icon }) => (
            <div key={num} className="stepper-step">
              <div className={cn(
                "stepper-circle",
                currentStep > num ? "completed" : currentStep === num ? "active" : "upcoming"
              )}>
                {currentStep > num ? <Icon className="w-5 h-5" /> : num}
              </div>
              <span className={cn(
                "stepper-label",
                currentStep === num && "active"
              )}>
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="card-bawarchi max-w-4xl mx-auto">
          <div className="p-6">
            {/* Step 1: Customer Information */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="mb-6">
                  <h2 className="card-bawarchi-title flex items-center gap-2" style={{ marginBottom: 'var(--spacing-2)' }}>
                    <User className="w-5 h-5" />
                    Customer Information
                  </h2>
                  <p className="text-small text-muted">
                    You can skip this step to browse menu and get pricing. Customer details are required before final submission.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-orange-700 font-medium text-sm">Full Name</Label>
                    <Input
                      id="name"
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                      className="border-orange-200 focus:border-orange-500 h-9 text-sm"
                      placeholder="Enter full name"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-orange-700 font-medium text-sm">Phone Number</Label>
                    <Input
                      id="phone"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                      className="border-orange-200 focus:border-orange-500 h-9 text-sm"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-orange-700 font-medium text-sm">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                      className="border-orange-200 focus:border-orange-500 h-9 text-sm"
                      placeholder="Enter email for promotional offers"
                    />
                    <p className="text-xs text-orange-500">Email helps us send you special offers and event reminders</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="prepared-by" className="text-orange-700 font-medium text-sm">Prepared By</Label>
                    <Input
                      id="prepared-by"
                      value={customerInfo.prepared_by}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, prepared_by: e.target.value }))}
                      className="border-orange-200 focus:border-orange-500 h-9 text-sm"
                      placeholder="Enter name of person preparing this quote"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="lead-employee" className="text-orange-700 font-medium text-sm">Lead By <span className="text-gray-400 font-normal">(optional)</span></Label>
                    <Input
                      id="lead-employee"
                      data-testid="lead-employee-input"
                      value={customerInfo.lead_employee || ''}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, lead_employee: e.target.value }))}
                      className="border-orange-200 focus:border-orange-500 h-9 text-sm"
                      placeholder="Employee who brought this lead (free text)"
                    />
                    <p className="text-xs text-orange-500">Helps track who originated this business</p>
                  </div>

                  {editingOrderId && (
                    <div className="space-y-1.5">
                      <Label htmlFor="update-reason" className="text-orange-700 font-medium text-sm">
                        Update Reason {orderStatus === 'confirmed' && <span className="text-red-600">*</span>}
                      </Label>
                      <Input
                        id="update-reason"
                        value={updateReason}
                        onChange={(e) => setUpdateReason(e.target.value)}
                        className="border-orange-200 focus:border-orange-500 h-9 text-sm"
                        placeholder={orderStatus === 'confirmed' ? "Required: Reason for editing confirmed order" : "Optional: Reason for update"}
                      />
                      {orderStatus === 'confirmed' && !updateReason && (
                        <p className="text-xs text-red-600">Update reason is required for confirmed orders</p>
                      )}
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label htmlFor="occasion" className="text-orange-700 font-medium text-sm">Occasion</Label>
                    <div className="relative">
                      <Input
                        id="occasion"
                        value={customerInfo.occasion}
                        onChange={(e) => {
                          setCustomerInfo(prev => ({ ...prev, occasion: e.target.value }));
                          setShowOccasionSuggestions(true);
                        }}
                        onFocus={() => setShowOccasionSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowOccasionSuggestions(false), 200)}
                        className="border-orange-200 focus:border-orange-500 h-9 text-sm"
                        placeholder="e.g., Wedding, Birthday, Corporate Event"
                        autoComplete="off"
                      />
                      {showOccasionSuggestions && filteredOccasionSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {filteredOccasionSuggestions.map((suggestion, idx) => (
                            <div
                              key={idx}
                              className="px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm"
                              onMouseDown={() => {
                                setCustomerInfo(prev => ({ ...prev, occasion: suggestion }));
                                setShowOccasionSuggestions(false);
                              }}
                            >
                              {suggestion}
                            </div>
                          ))}
                          {customerInfo.occasion && !filteredOccasionSuggestions.includes(customerInfo.occasion) && (
                            <div className="px-3 py-2 text-xs text-gray-500 border-t">
                              Press Enter to use &quot;{customerInfo.occasion}&quot; as new occasion
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="adults" className="text-orange-700 font-medium text-sm">Number of Adults</Label>
                    <div className="flex items-center space-x-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCustomerInfo(prev => ({ ...prev, adults: Math.max(1, prev.adults - 1) }))}
                        className="border-orange-300 hover:bg-orange-100"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        id="adults"
                        type="number"
                        value={customerInfo.adults}
                        onChange={(e) => setCustomerInfo(prev => ({ ...prev, adults: Math.max(1, parseInt(e.target.value) || 1) }))}
                        onFocus={(e) => e.target.select()}
                        onClick={(e) => e.target.select()}
                        className="w-20 text-center border-orange-200 focus:border-orange-500"
                        min="1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCustomerInfo(prev => ({ ...prev, adults: prev.adults + 1 }))}
                        className="border-orange-300 hover:bg-orange-100"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="kids" className="text-orange-700 font-medium text-sm">Number of Kids</Label>
                    <div className="flex items-center space-x-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCustomerInfo(prev => ({ ...prev, kids: Math.max(0, prev.kids - 1) }))}
                        className="border-orange-300 hover:bg-orange-100"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        id="kids"
                        type="number"
                        value={customerInfo.kids}
                        onChange={(e) => setCustomerInfo(prev => ({ ...prev, kids: Math.max(0, parseInt(e.target.value) || 0) }))}
                        onFocus={(e) => e.target.select()}
                        onClick={(e) => e.target.select()}
                        className="w-20 text-center border-orange-200 focus:border-orange-500"
                        min="0"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCustomerInfo(prev => ({ ...prev, kids: prev.kids + 1 }))}
                        className="border-orange-300 hover:bg-orange-100"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Multi-Day Event Planning Card */}
                <Card className="border-2 border-red-300 bg-gradient-to-r from-red-50 to-pink-50 shadow-md">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-red-800 flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4" />
                      Event Duration & Meals
                    </CardTitle>
                    <p className="text-xs text-red-600">Plan your event with meal selections (can select past dates up to 2 months for accounting)</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Date Range Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-red-700 font-medium text-sm">Start Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal border-red-300 hover:border-red-500 bg-white h-9 text-sm",
                                !customerInfo.start_date && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-red-600" />
                              {customerInfo.start_date ? format(customerInfo.start_date, "PPP") : "Pick start date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={customerInfo.start_date}
                              onSelect={(date) => {
                                const endDate = customerInfo.end_date && date > customerInfo.end_date ? date : customerInfo.end_date;
                                updateEventDates(date, endDate || date);
                              }}
                              disabled={(date) => {
                                const today = new Date();
                                const twoMonthsAgo = new Date(today);
                                twoMonthsAgo.setMonth(today.getMonth() - 2);
                                return date < twoMonthsAgo;
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-red-700 font-medium text-sm">End Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal border-red-300 hover:border-red-500 bg-white h-9 text-sm",
                                !customerInfo.end_date && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-red-600" />
                              {customerInfo.end_date ? format(customerInfo.end_date, "PPP") : "Pick end date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={customerInfo.end_date}
                              onSelect={(date) => {
                                updateEventDates(customerInfo.start_date, date);
                              }}
                              disabled={(date) => {
                                const twoMonthsAgo = new Date();
                                twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
                                const minDate = customerInfo.start_date || twoMonthsAgo;
                                return date < minDate;
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    {/* Event Days and Meal Selection */}
                    {eventDays.length > 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <span className="text-red-700 font-medium">Meal Selection for {eventDays.length} day{eventDays.length > 1 ? 's' : ''}:</span>
                          <Badge variant="outline" className="border-red-300 text-red-700">
                            {eventDays.length === 1 ? 'Single Day Event' : `${eventDays.length}-Day Event`}
                          </Badge>
                        </div>
                        
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                          {eventDays.map((day) => (
                            <Card key={day.day_number} className="border-orange-200 bg-white">
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-semibold text-orange-800">
                                    Day {day.day_number}: {day.display_date}
                                  </h4>
                                  <Badge variant="outline" className="border-orange-300">
                                    {day.selected_meals.length} meal{day.selected_meals.length !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                                
                                <div className="space-y-3">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {['breakfast', 'lunch', 'evening_snacks', 'dinner'].map((meal) => (
                                      <Button
                                        key={meal}
                                        variant={day.selected_meals.includes(meal) ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => toggleMealSelection(day.day_number, meal)}
                                        className={cn(
                                          "text-xs",
                                          day.selected_meals.includes(meal) 
                                            ? "bg-orange-600 hover:bg-orange-700 text-white"
                                            : "border-orange-300 text-orange-700 hover:bg-orange-100"
                                        )}
                                      >
                                        {meal === 'evening_snacks' ? 'Evening Snacks' : meal.charAt(0).toUpperCase() + meal.slice(1)}
                                      </Button>
                                    ))}
                                  </div>
                                  
                                  {/* Time Selection for Selected Meals */}
                                  {day.selected_meals.length > 0 && (
                                    <div className="space-y-2 p-3 bg-blue-50 rounded-lg">
                                      <Label className="text-sm font-medium text-blue-800">Delivery Times:</Label>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {day.selected_meals.map((meal) => (
                                          <div key={`${day.day_number}-${meal}`} className="flex items-center gap-2">
                                            <Label className="text-xs font-medium text-blue-700 min-w-20">
                                              {meal === 'evening_snacks' ? 'Evening:' : `${meal.charAt(0).toUpperCase() + meal.slice(1)}:`}
                                            </Label>
                                            <Input
                                              type="time"
                                              value={getMealTime(day.day_number, meal)}
                                              onChange={(e) => updateMealTime(day.day_number, meal, e.target.value)}
                                              className="text-xs border-blue-300 focus:border-blue-500"
                                            />
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Multi-Day Sub-Orders Option */}
                    {eventDays.length > 0 && eventDays.some(day => day.selected_meals.length > 0) && (
                      <Card className="border-purple-200 bg-purple-50 mt-4">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              id="multi-day-order"
                              checked={isMultiDayOrder}
                              onChange={(e) => {
                                setIsMultiDayOrder(e.target.checked);
                                if (e.target.checked) {
                                  // Initialize headcounts with default from customerInfo
                                  const initialHeadcounts = {};
                                  eventDays.forEach(day => {
                                    day.selected_meals.forEach(meal => {
                                      const key = `${day.day_number}-${meal}`;
                                      initialHeadcounts[key] = customerInfo.adults + customerInfo.kids;
                                    });
                                  });
                                  setDayMealHeadcounts(initialHeadcounts);
                                }
                              }}
                              className="mt-1 w-4 h-4 text-purple-600 border-purple-300 rounded focus:ring-purple-500"
                              data-testid="multi-day-order-checkbox"
                            />
                            <div className="flex-1">
                              <Label htmlFor="multi-day-order" className="text-purple-800 font-medium cursor-pointer">
                                Create Separate Sub-Orders per Day/Meal
                              </Label>
                              <p className="text-xs text-purple-600 mt-1">
                                Enable this to create separate orders for each day/meal combination. 
                                Each sub-order can be printed as a separate kitchen invoice while payment remains combined.
                              </p>
                            </div>
                          </div>
                          
                          {/* Headcount Grid per Day/Meal */}
                          {isMultiDayOrder && (
                            <div className="mt-4 space-y-3">
                              <Label className="text-purple-800 font-medium text-sm">Headcount per Day/Meal:</Label>
                              <div className="grid gap-3">
                                {eventDays.map(day => (
                                  day.selected_meals.length > 0 && (
                                    <div key={day.day_number} className="bg-white rounded-lg p-3 border border-purple-200">
                                      <div className="font-medium text-purple-800 text-sm mb-2">
                                        Day {day.day_number}: {day.display_date}
                                      </div>
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                        {day.selected_meals.map(meal => {
                                          const key = `${day.day_number}-${meal}`;
                                          return (
                                            <div key={key} className="flex items-center gap-2">
                                              <Label className="text-xs text-purple-700 min-w-16">
                                                {meal === 'evening_snacks' ? 'Eve Snacks' : meal.charAt(0).toUpperCase() + meal.slice(1)}:
                                              </Label>
                                              <Input
                                                type="number"
                                                min="1"
                                                value={dayMealHeadcounts[key] || ''}
                                                onChange={(e) => {
                                                  setDayMealHeadcounts(prev => ({
                                                    ...prev,
                                                    [key]: parseInt(e.target.value) || 0
                                                  }));
                                                }}
                                                className="w-20 h-8 text-xs border-purple-300 focus:border-purple-500"
                                                placeholder="# ppl"
                                                data-testid={`headcount-${key}`}
                                              />
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )
                                ))}
                              </div>
                              <p className="text-xs text-purple-500 italic">
                                Enter the number of people for each meal. Items added to cart will be split into separate sub-orders based on their assigned day/meal.
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>

                {/* Catering Type Selection */}
                <Card className="border-blue-200 bg-blue-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-blue-800 flex items-center gap-2">
                      <Utensils className="w-4 h-4" />
                      Catering Type
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="catering-type" className="text-blue-700 font-medium text-sm">Select Catering Type</Label>
                      <Select value={cateringType} onValueChange={setCateringType}>
                        <SelectTrigger className="border-blue-300 focus:border-blue-500 h-9">
                          <SelectValue placeholder="Choose catering type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="delivery">Delivery Only</SelectItem>
                          <SelectItem value="delivery_setup">Delivery & Setup</SelectItem>
                          <SelectItem value="in_house">In-House Service</SelectItem>
                          <SelectItem value="live">Live Catering</SelectItem>
                          <SelectItem value="pickup">Pick up</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {cateringType === 'other' && (
                      <div className="space-y-1.5">
                        <Label htmlFor="catering-description" className="text-blue-700 font-medium text-sm">Describe Catering Type</Label>
                        <Input
                          id="catering-description"
                          value={cateringTypeDescription}
                          onChange={(e) => setCateringTypeDescription(e.target.value)}
                          className="border-blue-300 focus:border-blue-500 h-9 text-sm"
                          placeholder="Please describe your catering needs"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Address field - only shown for delivery-related catering types */}
                {(cateringType === 'delivery' || cateringType === 'delivery_setup' || cateringType === 'live') && (
                  <div className="space-y-1.5">
                    <Label htmlFor="address" className="text-orange-700 font-medium text-sm">Event Address <span className="text-red-600">*</span></Label>
                    <Textarea
                      id="address"
                      value={customerInfo.address}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, address: e.target.value }))}
                      className="border-orange-200 focus:border-orange-500 text-sm"
                      placeholder="Enter complete address with city and zip code"
                      rows={2}
                    />
                  </div>
                )}

                {/* Backdating feature removed */}
              </div>
            )}

            {/* Step 2: Menu Selection */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <CardHeader className="p-0">
                  <CardTitle className="text-2xl text-orange-800 flex items-center gap-2">
                    <ShoppingCart className="w-6 h-6" />
                    Menu Selection
                  </CardTitle>
                </CardHeader>

                <div className="space-y-6">
                  {/* Smart Recommendations */}
                  {recommendedPackages.length > 0 && (
                    <Card className="border-2 border-orange-300 bg-gradient-to-r from-orange-50 to-red-50">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-lg text-orange-800 flex items-center gap-2">
                              <Sparkles className="w-5 h-5" />
                              Recommended Package for {customerInfo.occasion}
                            </CardTitle>
                            <p className="text-sm text-orange-600 mt-1">
                              {recommendedPackages[0]?.period_info || ''}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRecommendationsCollapsed(!recommendationsCollapsed)}
                            className="hover:bg-orange-100"
                          >
                            {recommendationsCollapsed ? <ChevronDown className="w-5 h-5 text-orange-700" /> : <ChevronUp className="w-5 h-5 text-orange-700" />}
                          </Button>
                        </div>
                      </CardHeader>
                      {!recommendationsCollapsed && (
                      <CardContent>
                        <div className="space-y-3">
                          {recommendedPackages[0]?.package_items?.slice(0, 4).map((item, index) => (
                            <div key={index} className="flex justify-between items-center">
                              <div>
                                <span className="font-medium text-sm">{item.menu_item_name}</span>
                                <span className="text-xs text-gray-500 ml-2">
                                  {item.quantity}x {item.tray_size.replace('_', ' ')}
                                </span>
                              </div>
                              <Badge variant="outline" className="text-xs border-orange-300">
                                Popular #{item.popularity_rank}
                              </Badge>
                            </div>
                          ))}
                          
                          <div className="pt-3 border-t border-orange-200">
                            <div className="flex justify-between items-center">
                              <span className="font-bold text-orange-800">
                                Complete Package: ${recommendedPackages[0].estimated_total.toFixed(2)}
                              </span>
                              <Button
                                onClick={addRecommendedPackage}
                                size="sm"
                                className="bg-orange-600 hover:bg-orange-700 text-white"
                              >
                                Add Package
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                      )}
                    </Card>
                  )}

                  {/* Day and Meal Selection for Adding Items */}
                  {eventDays.length > 0 && (() => {
                    // Calculate total number of events across all days
                    const totalEvents = eventDays.reduce((sum, day) => sum + day.selected_meals.length, 0);
                    const isMultiDayOrMultiEvent = eventDays.length > 1 || totalEvents > 1;
                    
                    // Only show the selector card if it's multi-day or multi-event
                    return isMultiDayOrMultiEvent ? (
                    <Card className="border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-4">
                          <CalendarIcon className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-blue-800">Adding items for:</span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Day Selection */}
                          <div className="space-y-2">
                            <Label className="text-blue-700 font-medium">Select Day</Label>
                            <Select 
                              value={selectedDayMeal.day.toString()} 
                              onValueChange={(value) => setSelectedDayMeal(prev => ({ ...prev, day: parseInt(value) }))}
                            >
                              <SelectTrigger className="border-blue-200 focus:border-blue-500">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {eventDays.map((day) => (
                                  <SelectItem key={day.day_number} value={day.day_number.toString()}>
                                    Day {day.day_number}: {day.display_date}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Meal Selection */}
                          <div className="space-y-2">
                            <Label className="text-blue-700 font-medium">Select Meal</Label>
                            <Select 
                              value={selectedDayMeal.meal} 
                              onValueChange={(value) => setSelectedDayMeal(prev => ({ ...prev, meal: value }))}
                            >
                              <SelectTrigger className="border-blue-200 focus:border-blue-500">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {eventDays
                                  .find(day => day.day_number === selectedDayMeal.day)
                                  ?.selected_meals.map((meal) => (
                                    <SelectItem key={meal} value={meal}>
                                      {meal === 'evening_snacks' ? 'Evening Snacks' : meal.charAt(0).toUpperCase() + meal.slice(1)}
                                    </SelectItem>
                                  )) || []}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Current Selection Display */}
                        <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-blue-800 font-medium">
                              Currently adding to: {eventDays.find(day => day.day_number === selectedDayMeal.day)?.display_date}
                            </span>
                            <Badge variant="outline" className="border-blue-300 text-blue-700">
                              {selectedDayMeal.meal === 'evening_snacks' ? 'Evening Snacks' : selectedDayMeal.meal.charAt(0).toUpperCase() + selectedDayMeal.meal.slice(1)}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    ) : null;
                  })()}

                  {/* Compact Filters and Search in Top Right Corner */}
                  <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end justify-between">
                    {/* Left side - Add Custom Item Button */}
                    <div>
                      <Button
                        onClick={async () => {
                          await fetchAllMenuItemsForDuplicateCheck(); // Fetch all items for duplicate check
                          await fetchAllMenuItems(); // Also fetch for UOM/size dropdowns
                          setShowCustomItemModal(true);
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white border-green-600"
                        size="default"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Custom Item
                      </Button>
                    </div>

                    {/* Right side - Compact Filters */}
                    <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
                      {/* Category Dropdown - Compact */}
                      <div className="w-full sm:w-48">
                        <Label className="text-orange-700 font-medium text-xs mb-1 block">Category</Label>
                        <Select value={selectedCategory} onValueChange={(value) => {
                          setSelectedCategory(value);
                          setMenuSearchQuery(''); // Clear search when selecting category
                          fetchMenuItems(value);
                        }}>
                          <SelectTrigger className="border-orange-200 focus:border-orange-500 h-9 text-sm">
                            <SelectValue placeholder="All categories" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((category) => (
                              <SelectItem key={category} value={category}>
                                {category}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Search Box - Compact */}
                      <div className="relative w-full sm:w-64">
                        <Label className="text-orange-700 font-medium text-xs mb-1 block">Search</Label>
                        <Search className="absolute left-3 bottom-2 w-4 h-4 text-orange-500" />
                        <Input
                          value={menuSearchQuery}
                          onChange={(e) => {
                            setMenuSearchQuery(e.target.value);
                            if (e.target.value) {
                              setSelectedCategory(''); // Clear category selection when searching
                              // Always fetch fresh items when searching
                              if (menuItems.length === 0) {
                                fetchAllMenuItems();
                              }
                            }
                          }}
                          onKeyDown={(e) => {
                            // Trigger search on Enter key
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (menuSearchQuery) {
                                setSelectedCategory(''); // Clear category selection
                                fetchAllMenuItems(); // Fetch all items for search
                              }
                            }
                          }}
                          onFocus={() => {
                            // Always ensure we have all items for searching
                            if (!selectedCategory) {
                              fetchAllMenuItems();
                            }
                          }}
                          placeholder="Search items..."
                          className="pl-10 pr-10 border-orange-200 focus:border-orange-500 h-9 text-sm"
                        />
                        {menuSearchQuery && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setMenuSearchQuery('');
                              setMenuItems([]); // Clear menu items to force reload
                              setSelectedCategory(''); // Clear category
                            }}
                            className="absolute right-1 bottom-1 h-7 w-7 p-0 hover:bg-orange-100"
                          >
                            <X className="w-3 h-3 text-orange-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Loading state for menu items */}
                  {loadingMenuItems && (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                      <span className="ml-3 text-orange-600">Loading menu items...</span>
                    </div>
                  )}

                  {/* Menu items display */}
                  {!loadingMenuItems && menuSearchQuery && getFilteredMenuItems().length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No items found matching &quot;{menuSearchQuery}&quot;</p>
                    </div>
                  )}

                  {!loadingMenuItems && !menuSearchQuery && !selectedCategory && menuItems.length === 0 && (
                    <div className="text-center py-8 border-2 border-dashed border-orange-300 rounded-lg bg-orange-50">
                      <Utensils className="w-12 h-12 text-orange-400 mx-auto mb-3" />
                      <p className="text-orange-600 font-medium">Select a category or search by name</p>
                      <p className="text-sm text-orange-500 mt-1">Choose from the dropdown above or use the search box</p>
                    </div>
                  )}

                  {!loadingMenuItems && selectedCategory && menuItems.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No items found in this category.</p>
                    </div>
                  )}

                  {!loadingMenuItems && getFilteredMenuItems().length > 0 && (
                    <div className="space-y-2">
                      {menuSearchQuery && (
                        <div className="flex items-center gap-2 text-sm text-orange-600 bg-orange-50 p-2 rounded">
                          <Search className="w-4 h-4" />
                          <span>Showing results for: <strong>&quot;{menuSearchQuery}&quot;</strong></span>
                        </div>
                      )}
                      {selectedCategory && !menuSearchQuery && (
                        <div className="flex items-center gap-2 text-sm text-orange-600 bg-orange-50 p-2 rounded">
                          <Utensils className="w-4 h-4" />
                          <span>Category: <strong>{selectedCategory}</strong></span>
                        </div>
                      )}
                      
                      {/* Compact Table/List View */}
                      <div className="border border-orange-200 rounded-lg overflow-hidden bg-white">
                        {/* Table Header */}
                        <div className="bg-orange-100 border-b border-orange-200 px-3 py-2 grid grid-cols-12 gap-2 items-center text-xs font-semibold text-orange-800">
                          <div className="col-span-3">Item Name</div>
                          <div className="col-span-2">Type / UOM</div>
                          <div className="col-span-2">Size/Option</div>
                          <div className="col-span-1 text-center">Qty</div>
                          <div className="col-span-2 text-right">Price</div>
                          <div className="col-span-2 text-center">Action</div>
                        </div>
                        
                        {/* Scrollable Table Body */}
                        <div className="max-h-[500px] overflow-y-auto">
                          {getFilteredMenuItems().map((item) => (
                            <MenuItemRow 
                              key={item.id} 
                              item={item} 
                              onAddToCart={addToCart}
                              recommendations={trayRecommendations}
                              addingToCart={addingToCart}
                              selectedDayMeal={selectedDayMeal}
                              eventDays={eventDays}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Review Cart */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <CardHeader className="p-0">
                  <CardTitle className="text-2xl text-orange-800 flex items-center gap-2">
                    <CheckCircle className="w-6 h-6" />
                    Review Your Order
                  </CardTitle>
                </CardHeader>

                {cart.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-lg">No items in cart. Go back to add items.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Multi-Day Cart Organization */}
                    {Object.entries(getCartByDayMeal())
                      .sort(([dayA], [dayB]) => parseInt(dayA) - parseInt(dayB))
                      .map(([dayNumber, dayMeals]) => {
                        const dayInfo = eventDays.find(day => day.day_number === parseInt(dayNumber));
                        const dayTotals = calculateDayMealTotals()[dayNumber];
                        
                        return (
                          <Card key={dayNumber} className="border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50">
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg text-blue-800">
                                  Day {dayNumber}: {dayInfo?.display_date || `Day ${dayNumber}`}
                                </CardTitle>
                                <Badge variant="outline" className="border-blue-400 text-blue-700 font-bold">
                                  Day Total: ${dayTotals?.dayTotal || 0}
                                </Badge>
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              {Object.entries(dayMeals)
                                .sort(([mealA], [mealB]) => {
                                  const mealOrder = ['breakfast', 'lunch', 'evening_snacks', 'dinner'];
                                  return mealOrder.indexOf(mealA) - mealOrder.indexOf(mealB);
                                })
                                .map(([mealType, mealItems]) => {
                                  const mealTotal = dayTotals?.[mealType] || 0;
                                  const mealDisplay = mealType === 'evening_snacks' ? 'Evening Snacks' : 
                                    mealType.charAt(0).toUpperCase() + mealType.slice(1);
                                  
                                  return (
                                    <Card key={`${dayNumber}-${mealType}`} className="border-orange-200 bg-white">
                                      <CardHeader className="pb-2">
                                        <div className="flex items-center justify-between">
                                          <h4 className="font-semibold text-orange-800 flex items-center gap-2">
                                            <Utensils className="w-4 h-4" />
                                            {mealDisplay}
                                          </h4>
                                          <Badge variant="outline" className="border-orange-300 text-orange-700">
                                            ${mealTotal}
                                          </Badge>
                                        </div>
                                      </CardHeader>
                                      <CardContent className="space-y-3">
                                        {mealItems.map((item, index) => {
                                          const globalIndex = cart.findIndex(cartItem => 
                                            cartItem.menu_item_id === item.menu_item_id && 
                                            cartItem.day_number === item.day_number && 
                                            cartItem.meal_type === item.meal_type &&
                                            cartItem.tray_size === item.tray_size
                                          );
                                          
                                          return (
                                            <Card key={`${item.menu_item_id}-${index}`} className="border-gray-200 bg-gray-50">
                                              <CardContent className="p-4">
                                                <div className="flex justify-between items-start">
                                                  <div className="flex-1">
                                                    <h5 className="font-medium text-gray-800">{item.menu_item_name}</h5>
                                                    <p className="text-sm text-gray-600">{item.category}</p>
                                                    
                                                    {/* Inline Editing Controls */}
                                                    <div className="flex items-center gap-4 mt-3 p-2 bg-orange-50 rounded-lg flex-wrap">
                                                      <div className="flex items-center gap-2">
                                                        <Label className="text-xs font-medium text-orange-700">Size:</Label>
                                                        <MenuItemSizeSelector
                                                          menuItemId={item.menu_item_id}
                                                          currentSize={item.tray_size}
                                                          onSizeChange={(newSize) => updateCartItemTraySize(globalIndex, newSize)}
                                                        />
                                                      </div>
                                                      <div className="flex items-center gap-2">
                                                        <Label className="text-xs font-medium text-orange-700">Qty:</Label>
                                                        <div className="flex items-center gap-1">
                                                          <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => updateCartItemQuantity(globalIndex, item.quantity - 1)}
                                                            className="h-6 w-6 p-0 border-orange-300 hover:bg-orange-100"
                                                          >
                                                            <Minus className="w-3 h-3" />
                                                          </Button>
                                                          <Input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => {
                                                              const value = parseInt(e.target.value) || 0;
                                                              updateCartItemQuantity(globalIndex, value);
                                                            }}
                                                            onFocus={(e) => e.target.select()}
                                                            className="w-16 h-6 text-center text-xs border-orange-300 focus:border-orange-500"
                                                            min="1"
                                                          />
                                                          <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => updateCartItemQuantity(globalIndex, item.quantity + 1)}
                                                            className="h-6 w-6 p-0 border-orange-300 hover:bg-orange-100"
                                                          >
                                                            <Plus className="w-3 h-3" />
                                                          </Button>
                                                        </div>
                                                      </div>
                                                      
                                                      <div className="flex items-center gap-2">
                                                        <Label className="text-xs font-medium text-orange-700">Price:</Label>
                                                        <div className="relative">
                                                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-orange-700 text-xs">$</span>
                                                          <Input
                                                            type="number"
                                                            value={item.unit_price}
                                                            onChange={(e) => {
                                                              const value = parseFloat(e.target.value) || 0;
                                                              updateCartItemPrice(globalIndex, value);
                                                            }}
                                                            onFocus={(e) => e.target.select()}
                                                            className="w-20 h-6 pl-5 text-xs border-orange-300 focus:border-orange-500"
                                                            step="0.01"
                                                            min="0"
                                                          />
                                                        </div>
                                                      </div>
                                                    </div>
                                                    
                                                    {/* Item Notes Section */}
                                                    <div className="mt-2">
                                                      {editingNoteIndex === globalIndex ? (
                                                        <div className="flex items-center gap-2">
                                                          <Input
                                                            type="text"
                                                            placeholder="e.g., Less spicy, extra plates..."
                                                            value={item.notes || ''}
                                                            onChange={(e) => updateCartItemNotes(globalIndex, e.target.value)}
                                                            onKeyDown={(e) => {
                                                              if (e.key === 'Enter') setEditingNoteIndex(null);
                                                            }}
                                                            className="flex-1 h-7 text-xs border-orange-300 focus:border-orange-500"
                                                            autoFocus
                                                            maxLength={150}
                                                            data-testid={`item-note-input-${globalIndex}`}
                                                          />
                                                          <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setEditingNoteIndex(null)}
                                                            className="h-7 px-2 text-green-600 hover:bg-green-50"
                                                          >
                                                            ✓
                                                          </Button>
                                                          <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                              updateCartItemNotes(globalIndex, '');
                                                              setEditingNoteIndex(null);
                                                            }}
                                                            className="h-7 px-2 text-red-500 hover:bg-red-50"
                                                          >
                                                            ✗
                                                          </Button>
                                                        </div>
                                                      ) : (
                                                        <div className="flex items-center gap-2">
                                                          {item.notes ? (
                                                            <div 
                                                              className="flex-1 text-xs text-orange-700 bg-orange-50 px-2 py-1 rounded cursor-pointer hover:bg-orange-100"
                                                              onClick={() => setEditingNoteIndex(globalIndex)}
                                                              data-testid={`item-note-display-${globalIndex}`}
                                                            >
                                                              📝 {item.notes}
                                                            </div>
                                                          ) : (
                                                            <button
                                                              onClick={() => setEditingNoteIndex(globalIndex)}
                                                              className="text-xs text-gray-500 hover:text-orange-600 flex items-center gap-1"
                                                              data-testid={`add-note-btn-${globalIndex}`}
                                                            >
                                                              <span className="text-sm">📝</span> Add note
                                                            </button>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                  
                                                  <div className="text-right ml-4">
                                                    <p className="font-bold text-lg text-orange-600 mb-1">
                                                      ${Math.floor(item.total_price)}
                                                    </p>
                                                    <div className="text-xs text-gray-500 mb-2">
                                                      {item.quantity} × ${Math.floor(item.unit_price)}
                                                    </div>
                                                    <Button
                                                      variant="outline"
                                                      size="sm"
                                                      onClick={() => removeFromCart(globalIndex)}
                                                      className="border-red-300 text-red-600 hover:bg-red-50 text-xs"
                                                    >
                                                      Remove
                                                    </Button>
                                                  </div>
                                                </div>
                                              </CardContent>
                                            </Card>
                                          );
                                        })}
                                      </CardContent>
                                    </Card>
                                  );
                                })}
                            </CardContent>
                          </Card>
                        );
                      })}
                    
                    {/* Grand Total Summary */}
                    <Card className="border-2 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
                      <CardContent className="p-6">
                        <div className="text-center">
                          <h3 className="text-2xl font-bold text-green-800 mb-2">Order Summary</h3>
                          <div className="space-y-2">
                            <div className="flex justify-between text-lg">
                              <span className="text-green-700">Event Duration:</span>
                              <span className="font-semibold text-green-800">
                                {eventDays.length} day{eventDays.length > 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="flex justify-between text-lg">
                              <span className="text-green-700">Total Items:</span>
                              <span className="font-semibold text-green-800">{cart.length}</span>
                            </div>
                            <Separator className="bg-green-300" />
                            <div className="flex justify-between text-xl font-bold">
                              <span className="text-green-800">Grand Total:</span>
                              <span className="text-green-800">${calculateSubtotal()}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Pricing & Fees */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <CardHeader className="p-0">
                  <CardTitle className="text-2xl text-orange-800 flex items-center gap-2">
                    <CreditCard className="w-6 h-6" />
                    Pricing & Discounts
                  </CardTitle>
                </CardHeader>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-orange-700 font-medium">Service Fee</Label>
                        <Select 
                          value={serviceFeeType} 
                          onValueChange={(value) => {
                            setServiceFeeType(value);
                            if (value !== 'custom') {
                              setCustomServiceFee(0);
                            }
                          }}
                        >
                          <SelectTrigger className="border-orange-200 focus:border-orange-500">
                            <SelectValue placeholder="Select service fee" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Service Fee</SelectItem>
                            <SelectItem value="5%">5% of Subtotal</SelectItem>
                            <SelectItem value="10%">10% of Subtotal</SelectItem>
                            <SelectItem value="12%">12% of Subtotal</SelectItem>
                            <SelectItem value="15%">15% of Subtotal</SelectItem>
                            <SelectItem value="custom">Custom Amount</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {serviceFeeType === 'custom' && (
                        <div className="space-y-2">
                          <Label htmlFor="custom-service-fee" className="text-orange-700 font-medium">Custom Service Fee ($)</Label>
                          <Input
                            id="custom-service-fee"
                            type="number"
                            value={customServiceFee}
                            onChange={(e) => setCustomServiceFee(parseFloat(e.target.value) || 0)}
                            onFocus={(e) => e.target.select()}
                            onClick={(e) => e.target.select()}
                            className="border-orange-200 focus:border-orange-500"
                            min="0"
                            step="0.01"
                            placeholder="Enter custom amount"
                          />
                        </div>
                      )}

                      {serviceFeeType !== 'none' && (
                        <div className="space-y-2">
                          <Label htmlFor="service-fee-description" className="text-orange-700 font-medium">Service Fee Description (Optional)</Label>
                          <Input
                            id="service-fee-description"
                            value={serviceFeeDescription}
                            onChange={(e) => setServiceFeeDescription(e.target.value)}
                            className="border-orange-200 focus:border-orange-500"
                            placeholder="e.g., Delivery Fee, Setup Charges, Service Charge"
                          />
                        </div>
                      )}

                      {serviceFeeType !== 'none' && serviceFeeType !== 'custom' && (
                        <div className="text-sm text-orange-600 bg-orange-50 p-2 rounded">
                          Service Fee: ${calculateServiceFee()} ({serviceFeeType} of ${calculateSubtotal()})
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-orange-700 font-medium">Discount Type</Label>
                        <Select 
                          value={discountType} 
                          onValueChange={(value) => {
                            setDiscountType(value);
                            if (value === 'none') {
                              setDiscountPercentage('');
                              setCustomDiscountAmount(0);
                            } else {
                              setDiscountPercentage(''); // Reset to empty when changing discount type to show placeholder
                            }
                          }}
                        >
                          <SelectTrigger className="border-orange-200 focus:border-orange-500">
                            <SelectValue placeholder="Select discount type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Discount</SelectItem>
                            <SelectItem value="cash_discount">Cash Discount</SelectItem>
                            <SelectItem value="promotional">Promotional Discount</SelectItem>
                            <SelectItem value="loyalty">Loyalty Discount</SelectItem>
                            <SelectItem value="other">Other Discount</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {discountType && discountType !== 'none' && (
                        <div className="space-y-2">
                          <Label className="text-orange-700 font-medium">Discount Percentage</Label>
                          <Select 
                            value={discountPercentage} 
                            onValueChange={(value) => {
                              setDiscountPercentage(value);
                              if (value !== 'custom') {
                                setCustomDiscountAmount(0);
                              }
                            }}
                          >
                            <SelectTrigger className="border-orange-200 focus:border-orange-500">
                              <SelectValue placeholder="Select discount percentage" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="5%">5% of Subtotal</SelectItem>
                              <SelectItem value="10%">10% of Subtotal</SelectItem>
                              <SelectItem value="12%">12% of Subtotal</SelectItem>
                              <SelectItem value="15%">15% of Subtotal</SelectItem>
                              <SelectItem value="20%">20% of Subtotal</SelectItem>
                              <SelectItem value="25%">25% of Subtotal</SelectItem>
                              <SelectItem value="custom">Custom Amount</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {discountType !== 'none' && discountPercentage === 'custom' && (
                        <div className="space-y-2">
                          <Label htmlFor="custom-discount-amount" className="text-orange-700 font-medium">Custom Discount Amount ($)</Label>
                          <Input
                            id="custom-discount-amount"
                            type="number"
                            value={customDiscountAmount}
                            onChange={(e) => setCustomDiscountAmount(parseFloat(e.target.value) || 0)}
                            onFocus={(e) => e.target.select()}
                            onClick={(e) => e.target.select()}
                            className="border-orange-200 focus:border-orange-500"
                            min="0"
                            step="0.01"
                            placeholder="Enter custom discount amount"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Advance Payment Section */}
                  <Card className="border-blue-200 bg-blue-50">
                    <CardHeader>
                      <CardTitle className="text-lg text-blue-800 flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        Advance Payment (Optional)
                      </CardTitle>
                      <p className="text-sm text-blue-600">
                        Record advance payment to calculate balance due
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="advance-payment" className="text-blue-700 font-medium">Advance Amount ($)</Label>
                          <Input
                            id="advance-payment"
                            type="number"
                            value={advancePayment}
                            onChange={(e) => setAdvancePayment(parseFloat(e.target.value) || 0)}
                            onFocus={(e) => e.target.select()}
                            onClick={(e) => e.target.select()}
                            className="border-blue-200 focus:border-blue-500"
                            min="0"
                            max={calculateTotal()}
                            step="1"
                            placeholder="Enter advance payment amount"
                          />
                          {advancePayment > calculateTotal() && (
                            <p className="text-sm text-red-600">
                              Advance cannot exceed total amount (${calculateTotal()})
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label className="text-blue-700 font-medium">Payment Method</Label>
                          <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                            <SelectTrigger className="border-blue-200 focus:border-blue-500">
                              <SelectValue placeholder="Select payment method" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cash">Cash</SelectItem>
                              <SelectItem value="card">Credit/Debit Card</SelectItem>
                              <SelectItem value="check">Check</SelectItem>
                              <SelectItem value="online">Online Transfer</SelectItem>
                              <SelectItem value="upi">UPI/Digital Wallet</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="payment-notes" className="text-blue-700 font-medium">Payment Notes (Optional)</Label>
                        <Textarea
                          id="payment-notes"
                          value={paymentNotes}
                          onChange={(e) => setPaymentNotes(e.target.value)}
                          className="border-blue-200 focus:border-blue-500"
                          placeholder="Add notes about the payment (check number, transaction ID, etc.)"
                          rows={2}
                        />
                      </div>

                      {advancePayment > 0 && (
                        <div className="bg-blue-100 p-3 rounded-lg">
                          <div className="flex justify-between items-center text-sm">
                            <span className="font-medium text-blue-800">Advance Payment:</span>
                            <span className="text-blue-900">${Math.floor(advancePayment)}</span>
                          </div>
                          <div className="flex justify-between items-center text-sm mt-1">
                            <span className="font-medium text-blue-800">Balance Due:</span>
                            <span className="text-blue-900 font-bold">${calculateBalanceAmount()}</span>
                          </div>
                          <div className="flex justify-between items-center text-xs mt-1 text-blue-600">
                            <span>Payment Date:</span>
                            <span>{new Date().toLocaleDateString()}</span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-orange-200 bg-orange-50">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>Subtotal:</span>
                          <span>${calculateSubtotal()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>
                            {serviceFeeDescription || 'Service Fee'}
                            {serviceFeeType !== 'none' && serviceFeeType !== 'custom' ? ` (${serviceFeeType})` : ''}
                            :
                          </span>
                          <span>${calculateServiceFee()}</span>
                        </div>
                        {calculateDiscountAmount() > 0 && (
                          <div className="flex justify-between text-green-600">
                            <span>Discount ({discountType}{discountPercentage && discountPercentage !== 'custom' ? ` - ${discountPercentage}` : ''}):</span>
                            <span>-${calculateDiscountAmount()}</span>
                          </div>
                        )}
                        <Separator />
                        <div className="flex justify-between text-xl font-bold text-orange-800">
                          <span>Total Amount:</span>
                          <span>${calculateTotal()}</span>
                        </div>
                        {advancePayment > 0 && (
                          <>
                            <div className="flex justify-between text-blue-600">
                              <span>Advance Payment:</span>
                              <span>-${Math.floor(advancePayment)}</span>
                            </div>
                            <div className="flex justify-between text-xl font-bold text-blue-800">
                              <span>Balance Due:</span>
                              <span>${calculateBalanceAmount()}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Step 5: Final Review */}
            {currentStep === 5 && !submittedOrderId && (
              <div className="space-y-6">
                <CardHeader className="p-0">
                  <CardTitle className="text-2xl text-orange-800 flex items-center gap-2">
                    <CheckCircle className="w-6 h-6" />
                    Final Review & Submit
                  </CardTitle>
                </CardHeader>

                {/* Validation Errors Display */}
                {(() => {
                  const missing = [];
                  if (!customerInfo.name) missing.push('Customer Name');
                  if (!customerInfo.phone) missing.push('Phone Number');
                  if (!customerInfo.prepared_by && !editingOrderId) missing.push('Prepared By');
                  if (!customerInfo.occasion) missing.push('Occasion');
                  if (!customerInfo.start_date) missing.push('Start Date');
                  if (!customerInfo.end_date) missing.push('End Date');
                  if (isAddressRequired() && !customerInfo.address) missing.push('Delivery Address');
                  if (!cateringType) missing.push('Catering Type');
                  if (cart.length === 0) missing.push('Menu Items (add at least 1 item)');
                  if (eventDays.length === 0) missing.push('Event Days');
                  if (!eventDays.some(day => day.selected_meals && day.selected_meals.length > 0)) missing.push('Meal Selection (select meals for event days)');
                  if (editingOrderId && orderStatus === 'confirmed' && !updateReason) missing.push('Update Reason (required for confirmed orders)');
                  
                  if (missing.length > 0) {
                    const needsUpdateReason = editingOrderId && orderStatus === 'confirmed' && !updateReason;
                    return (
                      <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-2">
                          <div className="text-red-600 font-semibold mt-0.5">⚠️</div>
                          <div className="flex-1">
                            <p className="text-red-800 font-semibold mb-2">Please complete the following required fields:</p>
                            <ul className="list-disc list-inside text-red-700 space-y-1">
                              {missing.map((field, idx) => (
                                <li key={idx}>{field}</li>
                              ))}
                            </ul>
                            {needsUpdateReason && (
                              <button
                                onClick={() => setCurrentStep(1)}
                                className="mt-3 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                              >
                                Go to Customer Information
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="border-orange-200">
                      <CardHeader>
                        <CardTitle className="text-lg text-orange-700">Customer Information</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <p><strong>Name:</strong> {customerInfo.name}</p>
                        <p><strong>Phone:</strong> {customerInfo.phone}</p>
                        {customerInfo.email && <p><strong>Email:</strong> {customerInfo.email}</p>}
                        <p><strong>Prepared By:</strong> {customerInfo.prepared_by}</p>
                        <p><strong>Occasion:</strong> {customerInfo.occasion}</p>
                        <p><strong>Guests:</strong> {customerInfo.adults} Adults, {customerInfo.kids} Kids</p>
                        <p><strong>Event Duration:</strong> {customerInfo.start_date && customerInfo.end_date ? `${format(customerInfo.start_date, 'PPP')} to ${format(customerInfo.end_date, 'PPP')}` : ''}</p>
                        {isAddressRequired() && <p><strong>Address:</strong> {customerInfo.address}</p>}
                      </CardContent>
                    </Card>

                    <Card className="border-orange-200">
                      <CardHeader>
                        <CardTitle className="text-lg text-orange-700">Order Summary</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <p><strong>Catering Type:</strong> {cateringType.replace('_', ' ')}</p>
                        <p><strong>Items:</strong> {cart.length}</p>
                        <p><strong>Subtotal:</strong> ${calculateSubtotal()}</p>
                        <p><strong>{serviceFeeDescription || 'Service Fee'}:</strong> ${calculateServiceFee()}{serviceFeeType !== 'none' && serviceFeeType !== 'custom' ? ` (${serviceFeeType})` : ''}</p>
                        {calculateDiscountAmount() > 0 && (
                          <p><strong>Discount:</strong> -${calculateDiscountAmount()}{discountPercentage && discountPercentage !== 'custom' ? ` (${discountPercentage})` : ''}</p>
                        )}
                        <p className="text-lg font-bold text-orange-800">
                          <strong>Total:</strong> ${calculateTotal()}
                        </p>
                        {advancePayment > 0 && (
                          <>
                            <p className="text-blue-600">
                              <strong>Advance Paid:</strong> ${Math.floor(advancePayment)} ({paymentMethod})
                            </p>
                            <p className="text-lg font-bold text-blue-800">
                              <strong>Balance Due:</strong> ${calculateBalanceAmount()}
                            </p>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-orange-700 font-medium">Order Status</Label>
                    <Select value={orderStatus} onValueChange={setOrderStatus}>
                      <SelectTrigger className="border-orange-200 focus:border-orange-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quote">Quote</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Post-Submission: Invoice Download */}
            {submittedOrderId && (
              <div className="space-y-6">
                <CardHeader className="p-0">
                  <CardTitle className="text-2xl text-green-800 flex items-center gap-2">
                    <CheckCircle className="w-6 h-6" />
                    Order Submitted Successfully!
                  </CardTitle>
                </CardHeader>

                <div className="text-center py-8">
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Order Confirmed!</h3>
                    <p className="text-gray-600">Order ID: {submittedOrderId.slice(0, 8)}...</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Total Amount: ${calculateTotal()}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto">
                    <Button
                      onClick={() => downloadInvoice('customer')}
                      className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Customer Invoice
                    </Button>
                    
                    <Button
                      onClick={() => downloadInvoice('kitchen')}
                      variant="outline"
                      className="border-orange-300 text-orange-700 hover:bg-orange-50 flex items-center gap-2"
                    >
                      <ChefHat className="w-4 h-4" />
                      Kitchen Copy
                    </Button>
                  </div>

                  <div className="mt-8">
                    <Button
                      onClick={() => {
                        setSubmittedOrderId(null);
                        setCurrentStep(1);
                        setCart([]);
                        setCustomerInfo({
                          name: '',
                          phone: '',
                          email: '',
                          prepared_by: '',
                          lead_employee: '',
                          occasion: '',
                          adults: 1,
                          kids: 0,
                          start_date: null,
                          end_date: null,
                          day_meal_selections: [],
                          address: ''
                        });
                        setEventDays([]);
                        setServiceFeeDescription('');
                        setMenuSearchQuery('');
                        setCateringType('');
                        setServiceFee(0);
                        setServiceFeeType('none');
                        setCustomServiceFee(0);
                        setDiscountType('none');
                        setDiscountPercentage('');
                        setDiscountAmount(0);
                        setCustomDiscountAmount(0);
                        setAdvancePayment(0);
                        setPaymentMethod('cash');
                        setPaymentNotes('');
                        setOrderStatus('quote');
                        setUpdateReason(''); // Clear update reason
                        setMenuItems([]);
                        setSelectedCategory('');
                        setRecommendedPackages([]);
                        setPopularItems([]);
                        setDatePickerOpen(false);
                        setTimePickerOpen(false);
                      }}
                      variant="ghost"
                      className="text-orange-600 hover:text-orange-700"
                    >
                      Create New Order
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons - Hidden after order submission */}
            {!submittedOrderId && (
              <div className="flex justify-between mt-6 pt-4 border-t border-orange-200">
                <Button
                  variant="outline"
                  onClick={handlePrevStep}
                  disabled={currentStep === 1}
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  Previous
                </Button>

                <div className="flex gap-2">
                {/* Save & Update button for editing mode on any step */}
                {editingOrderId && currentStep < 5 && (
                  <Button
                    onClick={submitOrder}
                    disabled={
                      loading || 
                      cart.length === 0 || 
                      submittedOrderId || 
                      !customerInfo.prepared_by ||
                      (orderStatus === 'confirmed' && !updateReason)
                    }
                    className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      orderStatus === 'confirmed' && !updateReason 
                        ? "Update reason required for confirmed orders" 
                        : !customerInfo.prepared_by 
                        ? "Prepared by field required" 
                        : ""
                    }
                  >
                    {loading ? 'Updating...' : 'Save & Update'}
                  </Button>
                )}
                
                {/* Show "Go to Final Review" button on Step 1 if user has visited Step 5 */}
                {currentStep === 1 && hasVisitedFinalReview && (
                  <Button
                    onClick={() => setCurrentStep(5)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Go to Final Review
                  </Button>
                )}
                
                {currentStep < 5 ? (
                  <Button
                    onClick={handleNextStep}
                    disabled={
                      (currentStep === 2 && cart.length === 0)
                    }
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Next
                  </Button>
                ) : (
                  <>
                    <div className="relative inline-block" title={(() => {
                      const missing = [];
                      if (!customerInfo.name) missing.push('Customer Name');
                      if (!customerInfo.phone) missing.push('Phone Number');
                      if (!customerInfo.prepared_by && !editingOrderId) missing.push('Prepared By');
                      if (!customerInfo.occasion) missing.push('Occasion');
                      if (!customerInfo.start_date) missing.push('Start Date');
                      if (!customerInfo.end_date) missing.push('End Date');
                      if (isAddressRequired() && !customerInfo.address) missing.push('Address');
                      if (!cateringType) missing.push('Catering Type');
                      if (cart.length === 0) missing.push('Menu Items');
                      if (eventDays.length === 0) missing.push('Event Days');
                      if (!eventDays.some(day => day.selected_meals && day.selected_meals.length > 0)) missing.push('Meal Selection');
                      if (editingOrderId && orderStatus === 'confirmed' && !updateReason) missing.push('Update Reason');
                      return missing.length > 0 ? `Missing: ${missing.join(', ')}` : '';
                    })()}>
                      <Button
                        onClick={submitOrder}
                        disabled={
                          loading || 
                          cart.length === 0 || 
                          submittedOrderId ||
                          // Require Step 1 to be complete before final submission
                          !customerInfo.name || 
                          !customerInfo.phone || 
                          (!customerInfo.prepared_by && !editingOrderId) ||
                          !customerInfo.occasion || 
                          !customerInfo.start_date || 
                          !customerInfo.end_date || 
                          (isAddressRequired() && !customerInfo.address) ||
                          !cateringType ||
                          eventDays.length === 0 ||
                          !eventDays.some(day => day.selected_meals && day.selected_meals.length > 0) ||
                          (editingOrderId && orderStatus === 'confirmed' && !updateReason)
                        }
                        className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? (editingOrderId ? 'Updating...' : 'Submitting...') : submittedOrderId ? (editingOrderId ? 'Order Updated' : 'Order Submitted') : (editingOrderId ? 'Update Order' : 'Submit Order')}
                      </Button>
                    </div>
                    <Button
                      onClick={() => {
                        setCurrentView('landing');
                        setEditingOrderId(null);
                        setViewingOrderId(null);
                      }}
                      className="btn-bawarchi-secondary"
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
              </div>
            )}
            
            {/* Validation messages */}
            {currentStep === 1 && editingOrderId && orderStatus === 'confirmed' && !updateReason && (
              <div className="mt-2 text-center text-sm text-red-600 bg-red-50 p-2 rounded">
                ⚠️ Update Reason is required for confirmed orders
              </div>
            )}
            
            {/* Step 5 validation message - Customer info required */}
            {currentStep === 5 && (
              !customerInfo.name || 
              !customerInfo.phone || 
              (!customerInfo.prepared_by && !editingOrderId) ||
              !customerInfo.occasion || 
              !customerInfo.start_date || 
              !customerInfo.end_date || 
              (isAddressRequired() && !customerInfo.address) ||
              !cateringType ||
              eventDays.length === 0 ||
              !eventDays.some(day => day.selected_meals && day.selected_meals.length > 0)
            ) && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-yellow-800 mb-2">Customer Information Required</h4>
                    <p className="text-sm text-yellow-700 mb-2">
                      Please complete Step 1 (Customer Information) before submitting your order. The following information is required:
                    </p>
                    <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
                      {!customerInfo.name && <li>Customer Name</li>}
                      {!customerInfo.phone && <li>Phone Number</li>}
                      {!customerInfo.occasion && <li>Occasion</li>}
                      {isAddressRequired() && !customerInfo.address && <li>Delivery Address</li>}
                      {(!customerInfo.prepared_by && !editingOrderId) && <li>Prepared By</li>}
                      {!customerInfo.start_date && <li>Event Start Date</li>}
                      {!customerInfo.end_date && <li>Event End Date</li>}
                      {!cateringType && <li>Catering Type</li>}
                      {eventDays.length === 0 && <li>Event Duration</li>}
                      {eventDays.length > 0 && !eventDays.some(day => day.selected_meals && day.selected_meals.length > 0) && <li>Meal Selection</li>}
                    </ul>
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="btn-bawarchi-secondary mt-3"
                      style={{ backgroundColor: 'var(--bawarchi-warning)', borderColor: 'var(--bawarchi-warning)', color: 'white' }}
                    >
                      Go to Customer Information
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Custom Item Modal */}
      <Dialog open={showCustomItemModal} onOpenChange={setShowCustomItemModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-orange-800">Add Custom Item</DialogTitle>
          </DialogHeader>
          
          {/* Scrollable Content Area */}
          <div className="space-y-4 overflow-y-auto max-h-[calc(90vh-120px)] pr-2">
            {/* Selected Day/Meal Display */}
            {eventDays.length > 0 && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-3">
                  <div className="text-sm text-blue-800">
                    <strong>Adding to:</strong> Day {selectedDayMeal.day} - {selectedDayMeal.meal.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Grid Layout - Same as Menu Management */}
            <div className="grid grid-cols-3 gap-4">
              {/* Item Name */}
              <div className="col-span-2">
                <Label>Item Name *</Label>
                <Input
                  value={customItem.name}
                  onChange={(e) => setCustomItem(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter custom item name"
                />
                {customItem.name && customItem.name.length >= 3 && (() => {
                  const similarItems = allMenuItemsForDuplicateCheck.filter(item => 
                    item.name.toLowerCase().includes(customItem.name.toLowerCase()) ||
                    customItem.name.toLowerCase().includes(item.name.toLowerCase())
                  ).slice(0, 5);
                  
                  return similarItems.length > 0 ? (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <p className="font-semibold text-yellow-800 mb-1">⚠️ Similar items found:</p>
                      <ul className="space-y-1">
                        {similarItems.map(item => (
                          <li key={item.id} className="text-yellow-700">
                            • {item.name} ({item.category}) - {item.unit_of_measure || 'N/A'}
                          </li>
                        ))}
                      </ul>
                      <p className="text-yellow-600 mt-1 italic">Consider using an existing item to avoid duplicates</p>
                    </div>
                  ) : null;
                })()}
              </div>
              
              {/* Category */}
              <div>
                <Label>Category *</Label>
                <Select 
                  value={customItem.category} 
                  onValueChange={(value) => setCustomItem(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.length > 0 ? (
                      categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="CUSTOM" disabled>Loading categories...</SelectItem>
                    )}
                    <SelectItem value="CUSTOM">CUSTOM (New Category)</SelectItem>
                  </SelectContent>
                </Select>
                {customItem.category === 'CUSTOM' && (
                  <Input 
                    className="mt-2"
                    placeholder="Enter new category name" 
                    value={customItem.customCategory || ''} 
                    onChange={(e) => setCustomItem(prev => ({ ...prev, customCategory: e.target.value }))} 
                  />
                )}
              </div>
              
              {/* Protein Type */}
              <div>
                <Label>Protein Type</Label>
                <Input
                  value={customItem.protein_type}
                  onChange={(e) => setCustomItem(prev => ({ ...prev, protein_type: e.target.value }))}
                  placeholder="e.g., Chicken, Veg, Goat"
                />
              </div>
              
              {/* Unit of Measure */}
              <div>
                <Label>Unit of Measure</Label>
                <Select value={customItem.unit_of_measure} onValueChange={(value) => {
                  setCustomItem(prev => ({ 
                    ...prev, 
                    unit_of_measure: value,
                    tray_size: '' // Reset size when UOM changes
                  }));
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select UOM" />
                  </SelectTrigger>
                  <SelectContent>
                    {getUniqueUOMs().length > 0 ? (
                      getUniqueUOMs().map(uom => (
                        <SelectItem key={uom} value={uom}>{uom}</SelectItem>
                      ))
                    ) : null}
                    <SelectItem value="Custom">Custom (New UOM)</SelectItem>
                  </SelectContent>
                </Select>
                {customItem.unit_of_measure === 'Custom' && (
                  <Input 
                    className="mt-2"
                    placeholder="Enter custom unit" 
                    value={customItem.customUOM || ''} 
                    onChange={(e) => setCustomItem(prev => ({ ...prev, customUOM: e.target.value }))} 
                  />
                )}
              </div>

              {/* Size/Tray Size - Cascade filtered by UOM OR Custom Sizes Builder */}
              <div>
                <Label>Size</Label>
                {customItem.unit_of_measure === 'Custom' ? (
                  <div className="text-xs text-gray-500 mt-1">Define custom sizes below</div>
                ) : (
                  <div className="text-xs text-gray-500 mt-1">Set prices &amp; select sizes below</div>
                )}
              </div>
            </div>

            {/* Custom Sizes Builder - Only shown when UOM is Custom */}
            {customItem.unit_of_measure === 'Custom' && (
              <div className="space-y-3 border border-orange-200 bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-orange-900">Define Sizes & Prices *</Label>
                  <span className="text-xs text-orange-700">Check to add to cart</span>
                </div>
                
                {customSizes.map((sizeItem, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border border-orange-200">
                    <input
                      type="checkbox"
                      checked={sizeItem.addToCart}
                      onChange={(e) => {
                        const newSizes = [...customSizes];
                        newSizes[index].addToCart = e.target.checked;
                        setCustomSizes(newSizes);
                      }}
                      className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                      title="Check to add this size to cart"
                    />
                    <Input
                      placeholder="Size name (e.g., Small, 10 people)"
                      value={sizeItem.size}
                      onChange={(e) => {
                        const newSizes = [...customSizes];
                        newSizes[index].size = e.target.value;
                        setCustomSizes(newSizes);
                      }}
                      className="flex-1"
                    />
                    <div className="relative w-32">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600">$</span>
                      <Input
                        type="number"
                        placeholder="Price"
                        value={sizeItem.price}
                        onChange={(e) => {
                          const newSizes = [...customSizes];
                          newSizes[index].price = e.target.value;
                          setCustomSizes(newSizes);
                        }}
                        className="pl-7"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    {customSizes.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCustomSizes(customSizes.filter((_, i) => i !== index));
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCustomSizes([...customSizes, { size: '', price: '', addToCart: false }])}
                  className="w-full border-orange-300 text-orange-700 hover:bg-orange-100"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Size
                </Button>
                
                <div className="text-xs text-orange-700 bg-orange-100 p-2 rounded">
                  <strong>Note:</strong> All sizes will be saved to your menu. Check the boxes for sizes you want to add to this order.
                </div>
              </div>
            )}

            {/* Pricing — parity with Menu Management Add New Item template.
                Renders different controls based on UOM:
                  - "Per Tray*": all 5 tray-size prices (QT/MHT/HT/MLT/LT)
                    with per-row "add to order" + qty.
                  - "Per Piece/Order/Person/Box/etc.": single price + qty.
                  - "Custom": rendered by Custom Sizes Builder above. */}
            {customItem.unit_of_measure && customItem.unit_of_measure !== 'Custom' && (() => {
              const uom = (customItem.unit_of_measure || '').toLowerCase();
              const isTrayUOM = uom.includes('tray');
              const TRAY_SIZES = [
                { key: 'qt', label: 'QT', desc: 'Quarter Tray' },
                { key: 'mht', label: 'MHT', desc: 'Medium Half Tray' },
                { key: 'ht', label: 'HT', desc: 'Half Tray' },
                { key: 'mlt', label: 'MLT', desc: 'Med Large Tray' },
                { key: 'lt', label: 'LT', desc: 'Large Tray' },
              ];
              return (
                <div className="space-y-3 border border-orange-200 bg-orange-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold text-orange-900">
                      Pricing &amp; Sizes *
                    </Label>
                    <span className="text-xs text-orange-700">Check sizes you want to add to this order</span>
                  </div>

                  {isTrayUOM ? (
                    <div className="space-y-2">
                      {TRAY_SIZES.map(({ key, label, desc }) => (
                        <div key={key} className="flex items-center gap-2 bg-white p-2 rounded border border-orange-200" data-testid={`custom-tray-row-${key}`}>
                          <input
                            type="checkbox"
                            data-testid={`custom-tray-add-${key}`}
                            checked={customItem.traySelection?.[key]?.add || false}
                            onChange={(e) => setCustomItem(prev => ({
                              ...prev,
                              traySelection: { ...prev.traySelection, [key]: { ...prev.traySelection[key], add: e.target.checked } },
                            }))}
                            className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                            title={`Add ${label} to this order`}
                          />
                          <div className="w-24 shrink-0">
                            <div className="text-xs font-semibold text-gray-800">{label}</div>
                            <div className="text-[10px] text-gray-500">{desc}</div>
                          </div>
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Price"
                              data-testid={`custom-tray-price-${key}`}
                              value={customItem.pricing?.[key] ?? ''}
                              onChange={(e) => setCustomItem(prev => ({
                                ...prev,
                                pricing: { ...prev.pricing, [key]: e.target.value },
                              }))}
                              className="pl-7"
                            />
                          </div>
                          <Input
                            type="number"
                            min="1"
                            placeholder="Qty"
                            data-testid={`custom-tray-qty-${key}`}
                            value={customItem.traySelection?.[key]?.qty ?? 1}
                            onChange={(e) => setCustomItem(prev => ({
                              ...prev,
                              traySelection: { ...prev.traySelection, [key]: { ...prev.traySelection[key], qty: parseInt(e.target.value) || 1 } },
                            }))}
                            className="w-20"
                          />
                        </div>
                      ))}
                      <p className="text-xs text-orange-700 bg-orange-100 p-2 rounded">
                        <strong>Note:</strong> All entered prices are saved to your menu. Only checked sizes are added to this order.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 bg-white p-2 rounded border border-orange-200">
                        <input
                          type="checkbox"
                          data-testid="custom-piece-add"
                          checked={customItem.pieceAdd !== false}
                          onChange={(e) => setCustomItem(prev => ({ ...prev, pieceAdd: e.target.checked }))}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                          title="Add to this order"
                        />
                        <Label className="text-xs text-gray-700 w-36 shrink-0">
                          {customItem.unit_of_measure} Price
                        </Label>
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Price"
                            data-testid="custom-piece-price"
                            value={customItem.pricing?.per_piece ?? ''}
                            onChange={(e) => setCustomItem(prev => ({
                              ...prev,
                              pricing: { ...prev.pricing, per_piece: e.target.value },
                            }))}
                            className="pl-7"
                          />
                        </div>
                        <Input
                          type="number"
                          min="1"
                          placeholder="Qty"
                          data-testid="custom-piece-qty"
                          value={customItem.pieceQty || 1}
                          onChange={(e) => setCustomItem(prev => ({ ...prev, pieceQty: parseInt(e.target.value) || 1 }))}
                          className="w-20"
                        />
                      </div>
                      <p className="text-xs text-orange-700 bg-orange-100 p-2 rounded">
                        <strong>Note:</strong> The price is saved to your menu. Uncheck to save without adding to this order.
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Notes (Optional)</Label>
              <Textarea
                value={customItem.notes}
                onChange={(e) => setCustomItem(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Special instructions or details..."
                className="border-orange-300 focus:border-orange-500"
                rows="2"
              />
            </div>

            <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
              <strong>Note:</strong> {customItem.unit_of_measure === 'Custom' 
                ? 'Item Name and at least one size with price are required. Check sizes to add to cart.'
                : 'Item Name and at least one price are required. Check the sizes you want to add to this order — all prices are saved to your menu for future use.'
              }
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setShowCustomItemModal(false)}
                variant="outline"
                className="flex-1 border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={addCustomItem}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                disabled={(() => {
                  if (!customItem.name) return true;
                  if (customItem.unit_of_measure === 'Custom') {
                    return customSizes.filter(s => s.size && s.price).length === 0;
                  }
                  const uom = (customItem.unit_of_measure || '').toLowerCase();
                  if (uom.includes('tray')) {
                    return !['qt','mht','ht','mlt','lt'].some(k => parseFloat(customItem.pricing?.[k]) > 0);
                  }
                  return !(parseFloat(customItem.pricing?.per_piece) > 0);
                })()}
              >
                {customItem.unit_of_measure === 'Custom' ? 'Save to Menu & Add to Cart' : 'Save to Menu & Add to Cart'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Welcome Hint Modal */}
      <WelcomeHintModal 
        isOpen={showWelcomeHint}
        onClose={() => setShowWelcomeHint(false)}
      />

      {/* Payment Selection Modal */}
      <PaymentSelectionModal 
        isOpen={showPaymentModal}
        planTier={pendingSignupData?.planId?.includes('premium') ? 'premium' : 'base'}
        onSelectBillingCycle={handleBillingCycleSelected}
        onCancel={() => {
          // For paid plans, cannot cancel - modal is blocking
          toast.warning('Please complete payment to access your account');
        }}
      />

      {/* Toaster for notifications */}
      <Toaster position="bottom-right" richColors offset="80px" />
      
      {/* Upgrade Modal - Moved to end to avoid Dialog portal conflicts */}
      <UpgradeModal
        key="upgrade-modal-root"
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        subscriptionStatus={subscriptionStatus}
        onPlanSelect={handlePlanSelect}
        currentUser={currentUser}
      />
    </div>
  );
}

export default App;
